import React, { useMemo, useCallback } from "react";
import { Dialog, DialogContent, Box, Typography, Button, IconButton, CircularProgress, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Tooltip, Pagination, Checkbox, LinearProgress, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { X, ArrowLeft } from "lucide-react";
import { StepLineIndicator } from "../ui/step-indicator";
import { SlideUpTransition } from "./mobile/mobile-bottom-sheet";
import ProvisionMatchTable, { ProvisionRow } from "./parts/provision-match-table";
import MatchTable, { MatchRow } from "./parts/match-table";
import { TreeDropdownOption } from "../tree-dropdown";
import { FileDetails } from "../../pages/transactions";
import Cookies from 'js-cookie';

import useApi from "../../hooks/use-api";
import buildApiRequestConfig from "../../api/url-builder";
import routes from "../../api/routes";
import { useUser } from "../../providers/user-provider";
import { useToast } from "../../providers/toaster-provider";
import { useSettings } from "../../providers/settings-provider";
import { exportToCSV } from "../../util/export";
import BusinessTypeDetectionModal from "./business-type-detection";
import AddBankAccountPopup from "./add-bank-account";
import { useBank, type ImportConfig, ImportConfigUtils } from "../../providers/bank-provider";
import { DropdownOption } from "../ui/dropdown";
import DateInput from "../ui/date-input";
import Player from 'lottie-react';
import okAnimation from '../../files/json/approved.json';
import searchAnimation from '../../files/json/search.json';
import searchingAnimation from '../../files/json/searching.json';

export interface ImportMatchPopupProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (approvedEntries: {
        entityId?: number;
        date?: string;
        amount?: number;
        description?: string;
        categoryId?: number;
        bankId?: number;
        linkedTransactionId?: string;
    }[], importId: string) => void;
    fileDetails?: FileDetails;
    onFileChange?: (details: FileDetails) => void;
    onBankChange?: (value: number | null) => void;
    provisions?: {
        id: string;
        date: string;
        amount: number;
        description: string;
        originalDescription?: string;
        category: string;
    }[] | undefined;
    bankId: number | null;
    progressModal: {
        open: boolean;
        currentChunk: number;
        totalChunks: number;
        processedTransactions: number;
        totalTransactions: number;
        status: 'processing' | 'success' | 'error';
    };
    categoriesExist?: boolean;
    isMobile?: boolean;
    provisionMatchConfig?: {
        amountPoints: number;
        amountTolerance: number;
        sameDayPoints: number;
        within1DayPoints: number;
        within3DayPoints: number;
        within7DayPoints: number;
        within30DayPoints: number;
        descriptionTokenThresholds: Array<{ minTokens: number; score: number }>;
        categoryBasePoints: number;
        categoryBonusPoints: number;
        minimumThreshold: number;
    };
}

const ImportMatchPopup: React.FC<ImportMatchPopupProps> = ({
    open,
    onClose,
    onConfirm,
    fileDetails,
    onFileChange,
    onBankChange,
    provisions,
    bankId,
    progressModal,
    categoriesExist,
    isMobile = false,
    provisionMatchConfig
}) => {
    const [matchedCount, setMatchedCount] = React.useState<{ duplicates: number, provision: number, category: number, unmatched: number }>({ duplicates: 0, provision: 0, category: 0, unmatched: 0 });
    const { user } = useUser();
    const { companySettings } = useSettings();
    const { formatDate, formatAmount } = useSettings();
    const { showError, showWarning } = useToast();

    const [step, setStep] = React.useState(0);
    const [importType, setImportType] = React.useState<'file' | 'basiq' | null>(null);
    const [duplicateCount, setDuplicateCount] = React.useState<number>(0);
    const [duplicateRows, setDuplicateRows] = React.useState<MatchRow[]>([]);
    const [filteredRows, setFilteredRows] = React.useState<any[]>([]);
    const [provisionMatchRows, setProvisionMatchRows] = React.useState<ProvisionRow[]>([]);
    const [categoryMatchRows, setCategoryMatchRows] = React.useState<MatchRow[]>([]);
    const [categoryUnmatchedRows, setCategoryUnmatchedRows] = React.useState<MatchRow[]>([]);
    const [originalUnmatchedRows, setOriginalUnmatchedRows] = React.useState<MatchRow[]>([]); // Preserve original unmatched from step 2
    const [isProcessing, setIsProcessing] = React.useState(false);
    const [categories, setCategories] = React.useState<TreeDropdownOption[]>([]);
    const [importedEntries, setImportedEntries] = React.useState<any[]>([]);
    const [importId, setImportId] = React.useState<string | null>(null);
    const [isCreatingImportEvent, setIsCreatingImportEvent] = React.useState(false);
    const [checkedStates, setCheckedStates] = React.useState<{ [step: number]: { [id: string]: boolean } }>({});
    const [hasProcessedStep, setHasProcessedStep] = React.useState<{ [key: number]: boolean }>({});
    const [businessTypeModal, setBusinessTypeModal] = React.useState(false);
    const [realTransactionCount, setRealTransactionCount] = React.useState<number | null>(null);
    const [categoryRefreshTrigger, setCategoryRefreshTrigger] = React.useState(0);
    const [isFileJustSelected, setIsFileJustSelected] = React.useState(false);
    const hasShownBusinessTypeModalRef = React.useRef(false);
    const hasShownAddBankModalRef = React.useRef(false);
    const lastRefreshTriggerRef = React.useRef(0);

    // Pagination states for steps 2, 3, and 4
    const [page, setPage] = React.useState<{ [step: number]: number }>({
        3: 1,
        4: 1,
        5: 1
    });
    const rowsPerPage = 50; // Can be adjusted based on performance needs

    const [fetchCategories, setFetchCategories] = useApi();
    const [createImport, setCreateImport] = useApi();
    const [fetchBasiqTransactions, setFetchBasiqTransactions] = useApi();
    const [checkBasiqUser, setCheckBasiqUser] = useApi();
    const [createBasiqUser, setCreateBasiqUser] = useApi();
    const [connectBasiqUser, setConnectBasiqUser] = useApi();
    
    // Feature flag: Enable/disable Basiq import option
    const enableBasiqImport = false;
    
    // Basiq states
    const [basiqDateRange, setBasiqDateRange] = React.useState<{ from: string; to: string }>({
        from: '',
        to: ''
    });
    const [userHasBasiq, setUserHasBasiq] = React.useState<boolean | null>(null);
    const [basiqUserId, setBasiqUserId] = React.useState<string | null>(null);
    const [basiqConsentCompleted, setBasiqConsentCompleted] = React.useState(false);
    
    // File selection state (Step 0)
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [file, setFile] = React.useState<File | null>(null);
    const [selectedBank, setSelectedBank] = React.useState<string | undefined>();
    const [bankOptions, setBankOptions] = React.useState<DropdownOption[]>([]);
    const [fileErrors, setFileErrors] = React.useState<{ bank?: string, file?: string }>({});
    const [showAddBankModal, setShowAddBankModal] = React.useState(false);
    const [fetchedBankData, setFetchedBankData] = React.useState<any[]>([]);
    const [shouldRefetchBanks, setShouldRefetchBanks] = React.useState(false);
    const { setBankAccounts, setSelectedBankAccount, setSelectedImportConfig: setContextImportConfig, selectedImportConfig } = useBank();
    const [fetchBankAccounts, setFetchBankAccounts] = useApi();
    const [createBankAccount, setCreateBankAccount] = useApi();
    const { showSuccess } = useToast();
    
    const isCheckingDuplicatesRef = React.useRef(false);
    const currentChunkRef = React.useRef(0);
    const totalChunksRef = React.useRef(0);
    
    // Progress tracking for duplicate check
    const [duplicateCheckProgress, setDuplicateCheckProgress] = React.useState<{ currentChunk: number; totalChunks: number; processedDuplicates: number; totalDuplicates: number }>({ currentChunk: 0, totalChunks: 0, processedDuplicates: 0, totalDuplicates: 0 });
    
    // Cache duplicate check results to avoid re-running API on step navigation
    const duplicateCheckCacheRef = React.useRef<{
        fileDataHash: string;
        duplicateRows: MatchRow[];
        filteredRows: any[];
        duplicateCount: number;
    } | null>(null);

    const utilityFunctions = useMemo(() => ({
        stripNumber: (s: string) => String(s ?? '').replace(/[^0-9.-]/g, ''),
        
        toNumber: (v?: any) => {
            if (v == null) return undefined;
            const n = parseFloat(String(v ?? '').replace(/[^0-9.-]/g, ''));
            return Number.isFinite(n) ? n : undefined;
        },
        
        parseDate: (v?: any) => {
            if (v == null) return undefined;

            const s = String(v).trim();
            if (!s || s === '-') return undefined;

            // First, try to extract YYYY-MM-DD format directly to avoid timezone issues
            const isoMatch = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
            if (isoMatch) {
                const year = parseInt(isoMatch[1], 10);
                const month = parseInt(isoMatch[2], 10);
                const day = parseInt(isoMatch[3], 10);
                if (year >= 1900 && year <= 2100 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                    // Create date using UTC to avoid timezone offset issues
                    const d = new Date(Date.UTC(year, month - 1, day));
                    if (!isNaN(d.getTime())) {
                        return d;
                    }
                }
            }

            let d = new Date(s);
            if (!isNaN(d.getTime())) {
                if (d.getFullYear() >= 1900 && d.getFullYear() <= 2100) {
                    return d;
                }
            }

            d = new Date(s.replace(/-/g, '/'));
            if (!isNaN(d.getTime()) && d.getFullYear() >= 1900 && d.getFullYear() <= 2100) {
                return d;
            }

            const currentYear = new Date().getFullYear();
            
            const monthMap: { [key: string]: number } = {
                'jan': 0, 'january': 0,
                'feb': 1, 'february': 1,
                'mar': 2, 'march': 2,
                'apr': 3, 'april': 3,
                'may': 4,
                'jun': 5, 'june': 5,
                'jul': 6, 'july': 6,
                'aug': 7, 'august': 7,
                'sep': 8, 'september': 8,
                'oct': 9, 'october': 9,
                'nov': 10, 'november': 10,
                'dec': 11, 'december': 11
            };

            const patterns = [
                { regex: /^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/, handler: (m: RegExpMatchArray) => new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10)) },
                { regex: /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/, handler: (m: RegExpMatchArray) => {
                    // Try DD/MM/YYYY first
                    const day = parseInt(m[1], 10);
                    const month = parseInt(m[2], 10) - 1;
                    const year = parseInt(m[3], 10);
                    
                    const testDate = new Date(year, month, day);
                    if (testDate.getDate() === day && testDate.getMonth() === month) {
                        return testDate;
                    }
                    // Try MM/DD/YYYY
                    return new Date(year, day - 1, month + 1);
                }},
                { regex: /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2})$/, handler: (m: RegExpMatchArray) => {
                    const day = parseInt(m[1], 10);
                    const month = parseInt(m[2], 10) - 1;
                    let year = parseInt(m[3], 10);
                    // Convert 2-digit year to 4-digit
                    year = year < 50 ? 2000 + year : 1900 + year;
                    return new Date(year, month, day);
                }},
                { regex: /^(\d{4})[/.-](\d{1,2})[/.-]([A-Za-z]+)$/, handler: (m: RegExpMatchArray) => {
                    const year = parseInt(m[1], 10);
                    const day = parseInt(m[2], 10);
                    const month = monthMap[m[3].toLowerCase()];
                    return month !== undefined ? new Date(year, month, day) : undefined;
                }},
                { regex: /^(\d{4})[/.-]([A-Za-z]+)[/.-](\d{1,2})$/, handler: (m: RegExpMatchArray) => {
                    const year = parseInt(m[1], 10);
                    const month = monthMap[m[2].toLowerCase()];
                    const day = parseInt(m[3], 10);
                    return month !== undefined ? new Date(year, month, day) : undefined;
                }},
                { regex: /^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})$/, handler: (m: RegExpMatchArray) => {
                    const day = parseInt(m[1], 10);
                    const month = monthMap[m[2].toLowerCase()];
                    const year = parseInt(m[3], 10);
                    return month !== undefined ? new Date(year, month, day) : undefined;
                }},
                { regex: /^([A-Za-z]{3,})\s+(\d{1,2})(?:\s+(\d{4}))?$/, handler: (m: RegExpMatchArray) => {
                    const month = monthMap[m[1].toLowerCase()];
                    const day = parseInt(m[2], 10);
                    const year = m[3] ? parseInt(m[3], 10) : currentYear;
                    return month !== undefined ? new Date(year, month, day) : undefined;
                }},
                { regex: /^(\d{1,2})[/.-]([A-Za-z]{3,})$/, handler: (m: RegExpMatchArray) => {
                    const day = parseInt(m[1], 10);
                    const month = monthMap[m[2].toLowerCase()];
                    return month !== undefined ? new Date(currentYear, month, day) : undefined;
                }},
                { regex: /^([A-Za-z]+)[/.-](\d{4})$/, handler: (m: RegExpMatchArray) => {
                    const month = monthMap[m[1].toLowerCase()];
                    const year = parseInt(m[2], 10);
                    return month !== undefined ? new Date(year, month, 1) : undefined;
                }},
                { regex: /^([A-Za-z]+)[/.-](\d{2})$/, handler: (m: RegExpMatchArray) => {
                    const month = monthMap[m[1].toLowerCase()];
                    let year = parseInt(m[2], 10);
                    year = year < 50 ? 2000 + year : 1900 + year;
                    return month !== undefined ? new Date(year, month, 1) : undefined;
                }},
                { regex: /^(\d{1,2})[/.-](\d{4})$/, handler: (m: RegExpMatchArray) => {
                    const month = parseInt(m[1], 10) - 1;
                    const year = parseInt(m[2], 10);
                    return new Date(year, month, 1);
                }},
                { regex: /^(\d{1,2})[/.-](\d{2})$/, handler: (m: RegExpMatchArray) => {
                    const month = parseInt(m[1], 10) - 1;
                    let year = parseInt(m[2], 10);
                    year = year < 50 ? 2000 + year : 1900 + year;
                    return new Date(year, month, 1);
                }},
                { regex: /^(\d{4})$/, handler: (m: RegExpMatchArray) => {
                    const year = parseInt(m[1], 10);
                    return new Date(year, 0, 1);
                }},
                { regex: /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})[T\s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/, handler: (m: RegExpMatchArray) => {
                    const day = parseInt(m[1], 10);
                    const month = parseInt(m[2], 10) - 1;
                    const year = parseInt(m[3], 10);
                    return new Date(year, month, day);
                }},
                { regex: /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2})[T\s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/, handler: (m: RegExpMatchArray) => {
                    const day = parseInt(m[1], 10);
                    const month = parseInt(m[2], 10) - 1;
                    let year = parseInt(m[3], 10);
                    year = year < 50 ? 2000 + year : 1900 + year;
                    return new Date(year, month, day);
                }},
                { regex: /^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})[T\s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/, handler: (m: RegExpMatchArray) => {
                    const year = parseInt(m[1], 10);
                    const month = parseInt(m[2], 10) - 1;
                    const day = parseInt(m[3], 10);
                    return new Date(year, month, day);
                }},
                { regex: /^(\d{1,2})[/.-]([A-Za-z]{3,})[/.-](\d{4})$/, handler: (m: RegExpMatchArray) => {
                    const day = parseInt(m[1], 10);
                    const month = monthMap[m[2].toLowerCase()];
                    const year = parseInt(m[3], 10);
                    return month !== undefined ? new Date(year, month, day) : undefined;
                }},
                { regex: /^(\d{1,2})[/.-]([A-Za-z]{3,})[/.-](\d{2})$/, handler: (m: RegExpMatchArray) => {
                    const day = parseInt(m[1], 10);
                    const month = monthMap[m[2].toLowerCase()];
                    let year = parseInt(m[3], 10);
                    year = year < 50 ? 2000 + year : 1900 + year;
                    return month !== undefined ? new Date(year, month, day) : undefined;
                }},
                { regex: /^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/, handler: (m: RegExpMatchArray) => {
                    const month = monthMap[m[1].toLowerCase()];
                    const day = parseInt(m[2], 10);
                    const year = parseInt(m[3], 10);
                    return month !== undefined ? new Date(year, month, day) : undefined;
                }},
                { regex: /^([A-Za-z]+)$/, handler: (m: RegExpMatchArray) => {
                    const month = monthMap[m[1].toLowerCase()];
                    return month !== undefined ? new Date(currentYear, month, 1) : undefined;
                }},
            ];

            for (const pattern of patterns) {
                const m = s.match(pattern.regex);
                if (m) {
                    const result = pattern.handler(m);
                    if (result && !isNaN(result.getTime()) && result.getFullYear() >= 1900 && result.getFullYear() <= 2100) {
                        return result;
                    }
                }
            }
            
            return undefined;
        },
        
        normalize: (v?: any) => (v == null ? '' : String(v).trim()),
        
        formatDate: (date: Date) => {
            // Use UTC methods to avoid timezone offset issues
            return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
        }
    }), []);

    const flattenCategories = (categories: TreeDropdownOption[]): Array<{id: string, label: string, searchStrings?: string}> => {
        const result: Array<{id: string, label: string, searchStrings?: string}> = [];
        
        const flatten = (cats: TreeDropdownOption[]) => {
            cats.forEach((cat: any) => {
                result.push({ id: cat.id, label: cat.label, searchStrings: cat.searchStrings });
                if (cat.children) {
                    flatten(cat.children);
                }
            });
        };
        
        flatten(categories);
        return result;
    };

    // Helper function to sort rows by date in descending order. Shared across
    // two different row shapes - MatchRow (category matching) has a
    // top-level `date`, but ProvisionRow (provision matching) nests it under
    // `imported.date` instead and has no top-level `date` at all. Reading
    // only `row.date` silently resolved to undefined for every provision
    // row, so this "sort" was a no-op there - rows rendered in raw
    // processing order instead of chronological order, making a provision's
    // several genuinely-matched transactions easy to miss if they landed far
    // apart in that unsorted, scrollable list instead of grouped together.
    const sortRowsByDateDesc = (rows: any[]): any[] => {
        return [...rows].sort((a, b) => {
            const dateA = utilityFunctions.parseDate(a.date ?? a.imported?.date) || new Date(0);
            const dateB = utilityFunctions.parseDate(b.date ?? b.imported?.date) || new Date(0);
            return dateB.getTime() - dateA.getTime();
        });
    };

    // Count matching tokens (words) between two descriptions
    const countMatchingTokens = (desc1: string, desc2: string): number => {
        if (!desc1 || !desc2) return 0;
        
        // Split into tokens (lowercase, alphanumeric only)
        const tokens1 = desc1.toLowerCase().split(/\W+/).filter(t => t.length > 0);
        const tokens2 = desc2.toLowerCase().split(/\W+/).filter(t => t.length > 0);
        
        // Count matching tokens (case-insensitive)
        let matchCount = 0;
        const usedIndices = new Set<number>();
        
        for (const token1 of tokens1) {
            for (let i = 0; i < tokens2.length; i++) {
                if (!usedIndices.has(i) && token1 === tokens2[i]) {
                    matchCount++;
                    usedIndices.add(i);
                    break;
                }
            }
        }
        
        return matchCount;
    };

    const findCategoryByLabel = useCallback((label: string) => {
        const flatCategories = flattenCategories(categories);
        return flatCategories.find(c => String(c.label) === String(label));
    }, [categories]);

    const findCategoryById = useCallback((id: string | number) => {
        const flatCategories = flattenCategories(categories);
        return flatCategories.find(c => String(c.id) === String(id));
    }, [categories]);

    const categoryLookups = useMemo(() => {
        if (!categories || categories.length === 0) {
            return { byName: new Map(), byId: new Map(), searchTokens: [], descriptionSearchTokens: [] };
        }

        const flatCategories = flattenCategories(categories);
        const byName = new Map();
        const byId = new Map();
        const searchTokens: { category: any, tokens: string[] }[] = [];
        // Separate, narrower token set used ONLY for matching against a
        // transaction's free-text description (see findMatchedCategory) -
        // built from each category's own explicitly-configured searchStrings
        // alone, never its display name. Description text is unstructured
        // and far likelier to incidentally contain a common word that also
        // happens to be part of a category's name (e.g. "Bank Fees" would
        // otherwise match any description merely containing "bank"), so
        // that name-derived matching is fine for the CSV's own structured
        // category column (searchTokens, below) but not for free text.
        const descriptionSearchTokens: { category: any, tokens: string[] }[] = [];

        flatCategories.forEach(cat => {
            byName.set(String(cat.label || '').toLowerCase(), cat);
            byId.set(String(cat.id || '').toLowerCase(), cat);

            // Combine label and searchStrings for tokens
            const labelAndSearch = [cat.label || '', cat.searchStrings || ''].join(' ');
            const tokens = labelAndSearch
                .split(/[\s,.-]+/)
                .map((s: string) => s.trim().toLowerCase())
                .filter((s: string) => s.length > 0);

            if (tokens.length > 0) {
                searchTokens.push({ category: cat, tokens });
            }

            const searchOnlyTokens = String(cat.searchStrings || '')
                .split(/[\s,.-]+/)
                .map((s: string) => s.trim().toLowerCase())
                .filter((s: string) => s.length > 0);

            if (searchOnlyTokens.length > 0) {
                descriptionSearchTokens.push({ category: cat, tokens: searchOnlyTokens });
            }
        });

        return { byName, byId, searchTokens, descriptionSearchTokens };
    }, [categories]);

    const findMatchedCategory = useCallback((categoryStr: string, description: string) => {
        if (!categoryStr && !description) return null;
        
        const catNorm = (categoryStr || '').toLowerCase();
        const descNorm = (description || '').toLowerCase();
        
        // First priority: match by category field if it exists and is not empty
        if (catNorm) {
            // First try exact match
            const nameMatch = categoryLookups.byName.get(catNorm);
            if (nameMatch) return nameMatch;
            
            const idMatch = categoryLookups.byId.get(catNorm);
            if (idMatch) return idMatch;
            
            // Then try comma-separated search strings
            const searchTokens = catNorm
                .split(/[\s,.-]+/)
                .map((s: string) => s.trim())
                .filter((s: string) => s.length > 0);
            
            for (const searchToken of searchTokens) {
                const tokenMatch = categoryLookups.byName.get(searchToken);
                if (tokenMatch) return tokenMatch;
                
                // Try matching against category tokens
                for (const { category, tokens } of categoryLookups.searchTokens) {
                    if (tokens.includes(searchToken)) {
                        return category;
                    }
                }
            }
        }
        
        // Second priority: try matching by description if category field didn't work.
        // Only checked against each category's own explicitly-configured
        // searchStrings (descriptionSearchTokens) - NOT its display name/byName
        // lookup - free text is too likely to incidentally contain a word
        // that's also part of an unrelated category's name (e.g. "Bank Fees"
        // matching any description that happens to mention "bank").
        if (descNorm) {
            // Split description by commas to handle multiple keywords
            const descTokens = descNorm
                .split(/[\s,.-]+/)
                .map((s: string) => s.trim())
                .filter((s: string) => s.length > 0);

            for (const descToken of descTokens) {
                for (const { category, tokens } of categoryLookups.descriptionSearchTokens) {
                    if (tokens.includes(descToken)) {
                        return category;
                    }
                }
            }
        }
        
        return null;
    }, [categoryLookups]);

    // Bank account management (Step 0)
    const refetchBankAccounts = React.useCallback(() => {
        if (!user) return;
        const { url, method } = buildApiRequestConfig(routes.bankAccounts.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
        setFetchBankAccounts({ url, method });
    }, [user, setFetchBankAccounts]);

    React.useEffect(() => {
        refetchBankAccounts();
    }, [user, refetchBankAccounts, shouldRefetchBanks, open]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetchBankAccounts;
        if (data && !isLoading) {
            setFetchedBankData(data);
            setBankAccounts(data);
            const options = data.map((bank: any) => ({ label: `${bank.bank} (${bank.name})`, value: String(bank.id) }));
            setBankOptions(options);
        }
        if (error) {
            showError(error ? error.toString() : 'Failed to fetch bank accounts');
        }
    }, [fetchBankAccounts, showError, setBankAccounts]);

    React.useEffect(() => {
        if (open && bankOptions.length === 0 && !fetchBankAccounts.isLoading && !hasShownAddBankModalRef.current) {
            hasShownAddBankModalRef.current = true;
            setShowAddBankModal(true);
        }
    }, [open, bankOptions, fetchBankAccounts.isLoading]);

    React.useEffect(() => {
        const { data, isLoading, error } = createBankAccount;
        if (data && !isLoading) {
            setShowAddBankModal(false);
            setSelectedBank(undefined);
            setShouldRefetchBanks(prev => !prev);
            showSuccess('Bank account added successfully');
        }
        if (error) {
            showError(error ? error.toString() : 'Failed to create bank account');
        }
    }, [createBankAccount, showError, showSuccess]);

    React.useEffect(() => {
        if (bankOptions.length === 1 && !selectedBank) {
            const bankObj = bankOptions[0];
            setSelectedBank(bankObj.value);
            const selectedBankData = fetchedBankData.find(b => String(b.id) === bankObj.value);
            if (selectedBankData) {
                setSelectedBankAccount(selectedBankData);
                try {
                    const importConfig = JSON.parse(selectedBankData.importConfig) as ImportConfig;
                    setContextImportConfig(importConfig);
                } catch (e) {
                    console.error('Failed to parse importConfig:', e);
                }
            }
            onBankChange?.(typeof bankObj.value === 'string' ? parseInt(bankObj.value, 10) : bankObj.value);
        }
    }, [bankOptions, onBankChange, selectedBank, fetchedBankData, setSelectedBankAccount, setContextImportConfig]);

    React.useEffect(() => {
        // Only fetch if this is a new refresh trigger or when modal first opens
        if (categoryRefreshTrigger !== lastRefreshTriggerRef.current || open) {
            lastRefreshTriggerRef.current = categoryRefreshTrigger;
            const { url, method } = buildApiRequestConfig(routes.categories.fetchByEntity, [{ param: 'entityId', value: user?.entityId }]);
            setFetchCategories({ url, method });
        }
    }, [open, user, setFetchCategories, categoryRefreshTrigger]);

    // Reset step when modal opens - start at step 0 (file selection)
    React.useEffect(() => {
        if (open) {
            // Reset all refs and state when opening
            setImportType(null);
            hasShownBusinessTypeModalRef.current = false;
            hasShownAddBankModalRef.current = false;
            setStep(0);
            setImportedEntries([]);
            setProvisionMatchRows([]);
            setCategoryMatchRows([]);
            setCategoryUnmatchedRows([]);
            setOriginalUnmatchedRows([]);
            setMatchedCount({ duplicates: 0, provision: 0, category: 0, unmatched: 0 });
            setImportId(null);
            setIsCreatingImportEvent(false);
            setHasProcessedStep({});
            setCheckedStates({});
            setDuplicateRows([]);
            setFilteredRows([]);
            setDuplicateCheckProgress({ currentChunk: 0, totalChunks: 0, processedDuplicates: 0, totalDuplicates: 0 });
            duplicateCheckCacheRef.current = null; // Clear duplicate check cache when new file is opened
            isCheckingDuplicatesRef.current = false;
            // Reset pagination
            setPage({ 3: 1, 4: 1, 5: 1 });
            // Reset file selection
            setFile(null);
            setFileErrors({});
            setIsFileJustSelected(false);
            // Reset Basiq states
            setUserHasBasiq(null);
            setBasiqDateRange({ from: '', to: '' });
            setBasiqUserId(null);
            setBasiqConsentCompleted(false);
        } else {
            // Reset refs when modal closes (already done above, but keeping for clarity)
            setImportType(null);
            hasShownBusinessTypeModalRef.current = false;
            hasShownAddBankModalRef.current = false;
            setIsFileJustSelected(false);
            setUserHasBasiq(null);
            setBasiqDateRange({ from: '', to: '' });
            setBasiqUserId(null);
            setBasiqConsentCompleted(false);
            setUserHasBasiq(null);
            setBasiqDateRange({ from: '', to: '' });
        }
    }, [open, companySettings]);

    // Parse file when selected
    React.useEffect(() => {
        if (!file || !selectedImportConfig || importType === 'basiq') return;

        const readFileAsText = (f: File): Promise<string> =>
            new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = () => reject(reader.error);
                reader.readAsText(f, 'utf-8');
            });

        const detectSeparator = (sample: string): string => {
            const candidates = [',', '\t', ';', '|'];
            const scores = candidates.map(sep => {
                const lines = sample.split(/\r\n|\n|\r/).slice(0, 5);
                const total = lines.reduce((acc, l) => acc + (l.split(sep).length - 1), 0);
                return { sep, total };
            });
            scores.sort((a, b) => b.total - a.total);
            return scores[0].total > 0 ? scores[0].sep : ',';
        };

        const parseDelimited = (text: string, sep: string): string[][] => {
            const rows: string[][] = [];
            let cur = '';
            let row: string[] = [];
            let inQuotes = false;

            for (let i = 0; i < text.length; i++) {
                const ch = text[i];

                if (inQuotes) {
                    if (ch === '"') {
                        if (text[i + 1] === '"') {
                            cur += '"';
                            i++;
                        } else {
                            inQuotes = false;
                        }
                    } else {
                        cur += ch;
                    }
                } else {
                    if (ch === '"') {
                        inQuotes = true;
                    } else if (ch === sep) {
                        row.push(cur);
                        cur = '';
                    } else if (ch === '\r') {
                        // ignore
                    } else if (ch === '\n') {
                        row.push(cur);
                        rows.push(row);
                        row = [];
                        cur = '';
                    } else {
                        cur += ch;
                    }
                }
            }

            if (cur !== '' || row.length > 0) {
                row.push(cur);
                rows.push(row);
            }

            return rows;
        };

        (async () => {
            try {
                const name = file.name || '';
                const ext = name.split('.').pop()?.toLowerCase();
                if (!['csv', 'tsv', 'txt'].includes(ext || '')) {
                    showError('Only CSV/TSV/TXT text formats are parsed in this UI.');
                    return;
                }

                const text = await readFileAsText(file);
                if (!text) {
                    showError('No data found in the file.');
                    return;
                }

                const sample = text.split(/\r\n|\n|\r/).slice(0, 10).join('\n');
                const sep = ext === 'tsv' ? '\t' : detectSeparator(sample);
                const rows = parseDelimited(text, sep);

                if (!rows.length) {
                    showError('No rows parsed from the file.');
                    return;
                }

                let headers: (string | number)[] = [];
                let dataRows: string[][] = [];

                if (selectedImportConfig.header) {
                    headers = rows[0].map((cell, i) => (cell ?? '').toString().trim() || `column_${i}`);
                    dataRows = rows.slice(1);
                } else {
                    headers = rows[0].map((_, i) => i);
                    dataRows = rows;
                }

                const parsedRows = dataRows.map((row) => {
                    if (selectedImportConfig.header) {
                        const obj: Record<string, string> = {};
                        (headers as string[]).forEach((header, i) => {
                            obj[header] = (row[i] ?? '').toString().trim();
                        });
                        return obj;
                    } else {
                        return row;
                    }
                }) as Array<Record<string, string> | string[]>;

                const headerValidation = ImportConfigUtils.validateHeaders(
                    headers as string[],
                    selectedImportConfig
                );
                if (!headerValidation.valid) {
                    setFileErrors({ file: `${headers.join(', ')}` });
                    setIsFileJustSelected(false);
                    return;
                }

                setFileErrors({});
                // File is now compliant with the bank's import format
                setIsFileJustSelected(true);

                const validRows: any[] = [];
                const errorRows: Array<{ row: any; errors: string[] }> = [];

                parsedRows.forEach((row, index) => {
                    const validation = ImportConfigUtils.validateRow(row, selectedImportConfig);
                    if (validation.valid) {
                        const transformed = ImportConfigUtils.transformRow(row, selectedImportConfig);
                        if (transformed) {
                            validRows.push(transformed);
                        }
                    } else {
                        errorRows.push({ row, errors: validation.errors });
                    }
                });

                const futureRows = errorRows.filter(e => e.errors.some(err => err.endsWith('is in the future'))).length;
                if (futureRows) showWarning(`${futureRows} transaction${futureRows === 1 ? ' is' : 's are'} dated in the future and won't be imported.`);

                onFileChange?.({
                    file,
                    parsedRows: validRows,
                    failedRows: errorRows.map(e => e.row),
                    result: {
                        successfulRows: validRows.length,
                        failedRows: errorRows.length,
                        totalRows: rows.length
                    },
                    headers: headers as string[]
                } as FileDetails);
            } catch (err: any) {
                showError(err?.toString?.() ?? 'Failed to parse file');
                setIsFileJustSelected(false);
            }
        })();
    }, [file, selectedImportConfig, showError, showWarning, onFileChange]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetchCategories;
        if (!isLoading && data) {
            const transformForTree = (cat: any): any => ({
                id: cat.id.toString(),
                label: cat.name,
                searchStrings: cat.searchString,
                children: cat.children ? cat.children.map(transformForTree) : undefined
            });
            
            const hierarchicalCategories = data.map(transformForTree);
            // Only show business type modal if:
            // 1. No categories exist
            // 2. Haven't shown modal yet this session
            // 3. A file is currently selected AND just validated
            // 4. File has parsed rows
            if (data.length === 0 && !hasShownBusinessTypeModalRef.current && isFileJustSelected && file && fileDetails?.parsedRows && fileDetails.parsedRows.length > 0) {
                hasShownBusinessTypeModalRef.current = true;
                setBusinessTypeModal(true);
            }
            setCategories(hierarchicalCategories);
        } else if (!isLoading && data === null) {
            setCategories([]);
        }
        if (error) {
            showError("Failed to load categories for matching.");
        }
    }, [fetchCategories, showError, isFileJustSelected, file, fileDetails]);

    const approvedForImport = useMemo(() => {
        if (!bankId || !user) return [];

        const approved: any[] = [];
        const addedIds = new Set<string>(); // Track IDs to prevent duplicates

        // Step 1: Only add CHECKED rows from provision matching step
        const checkedProvisionRows = provisionMatchRows.filter(r => r.checked);
        checkedProvisionRows.forEach(r => {
            const rowId = r.id;
            if (addedIds.has(rowId)) return; // Skip if already added
            addedIds.add(rowId);
            const date = utilityFunctions.parseDate(r.imported?.date ?? r.provision?.date);
            const amount = utilityFunctions.toNumber(r.imported?.amount ?? r.provision?.amount);
            
            let categoryId = undefined;
            if (r.provision?.category && r.provision.category !== '-') {
                const foundCategory = findCategoryByLabel(String(r.provision!.category));
                categoryId = foundCategory?.id;
            }
            if (!categoryId) {
                const categoryStr = r.imported?.category;
                if (categoryStr) {
                    const foundCategory = findCategoryByLabel(String(categoryStr));
                    categoryId = foundCategory?.id;
                }
            }
            
            approved.push({
                entityId: user?.entityId,
                date: date ? utilityFunctions.formatDate(date) : undefined,
                amount,
                description: r.imported?.description ?? r.provision?.description,
                categoryId,
                bankId,
                linkedTransactionId: r.provision?.id || null,
                source: 'provision',
                sourceId: r.id,
                sourceData: r.imported?.sourceData,
                dateForSort: date
            });
        });

        // Step 2: Only add CHECKED rows from category matching step (auto-matched categories)
        const checkedCategoryRows = categoryMatchRows.filter(r => r.checked);
        checkedCategoryRows.forEach(r => {
            if (addedIds.has(r.id)) return; // Skip if already added
            addedIds.add(r.id);
            const date = utilityFunctions.parseDate(r.date);
            const amount = utilityFunctions.toNumber(r.amount);
            const category = findCategoryByLabel(String(r.suggestedCategory)) 
                || findCategoryById(String(r.suggestedCategory));
            
            approved.push({
                entityId: user?.entityId,
                date: date ? utilityFunctions.formatDate(date) : undefined,
                amount,
                description: r.description,
                categoryId: category?.id,
                bankId,
                source: 'category',
                sourceId: r.id,
                sourceData: r.sourceData,
                dateForSort: date
            });
        });

        // Step 3: Only add CHECKED rows from manual category step (unmatched rows)
        const checkedUnmatchedRows = categoryUnmatchedRows.filter(r => r.checked);
        checkedUnmatchedRows.forEach(r => {
            if (addedIds.has(r.id)) return; // Skip if already added
            addedIds.add(r.id);
            const date = utilityFunctions.parseDate(r.date);
            const amount = utilityFunctions.toNumber(r.amount);
            
            // Only lookup category if suggestedCategory is not empty/blank
            let categoryId: string | number | undefined = undefined;
            if (r.suggestedCategory && r.suggestedCategory.trim() !== '') {
                const category = findCategoryByLabel(String(r.suggestedCategory))
                    || findCategoryById(String(r.suggestedCategory));
                categoryId = category?.id;
            }
            
            approved.push({
                entityId: user?.entityId,
                date: date ? utilityFunctions.formatDate(date) : undefined,
                amount,
                description: r.description,
                categoryId,
                bankId,
                source: 'unmatched',
                sourceId: r.id,
                sourceData: r.sourceData,
                dateForSort: date
            });
        });

        // Sort all approved transactions by date in descending order
        approved.sort((a, b) => {
            const dateA = a.dateForSort || (a.date ? new Date(a.date) : new Date(0));
            const dateB = b.dateForSort || (b.date ? new Date(b.date) : new Date(0));
            return dateB.getTime() - dateA.getTime();
        });

        // Remove the temporary dateForSort property
        return approved.map(({ dateForSort, ...rest }) => rest);
    }, [provisionMatchRows, categoryMatchRows, categoryUnmatchedRows, user, bankId, utilityFunctions, findCategoryById, findCategoryByLabel]);

    const processDuplicatesInChunks = useCallback(async (parsedRows: any[], chunkSize = 100) => {
        if (!user?.entityId || isCheckingDuplicatesRef.current) {
            setIsProcessing(false);
            return;
        }

        isCheckingDuplicatesRef.current = true;
        currentChunkRef.current = 0;
        const duplicateCounts: number[] = [];
        const nonDuplicateRows: any[] = [];
        const foundDuplicateRows: any[] = [];

        const totalRows = parsedRows.length;
        const chunks: any[][] = [];
        
        // Pre-create all chunks
        for (let i = 0; i < totalRows; i += chunkSize) {
            const chunk = parsedRows.slice(i, i + chunkSize);
            const chunkData = chunk.map((row, rowIndex) => ({
                date: utilityFunctions.normalize(row['Date'] ?? row.date),
                amount: utilityFunctions.toNumber(row['Amount'] ?? row.amount),
                description: utilityFunctions.normalize(row['Description'] ?? row.description),
                originalRow: row,
                originalIndex: i + rowIndex // Store absolute index
            })).filter(item => item.date && item.amount != null && !isNaN(item.amount) && item.description);
            
            if (chunkData.length > 0) {
                chunks.push(chunkData);
            }
        }
        
        totalChunksRef.current = chunks.length;
        
        // Calculate total transactions to process (sum of all filtered chunks)
        const totalTransactionsToProcess = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
        
        // Initialize progress
        setDuplicateCheckProgress({ currentChunk: 0, totalChunks: chunks.length, processedDuplicates: 0, totalDuplicates: totalTransactionsToProcess });

        // Track total transactions processed across all chunks
        let totalTransactionsProcessed = 0;

        // Process chunks sequentially using direct fetch
        for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
            try {
                currentChunkRef.current = chunkIndex;
                const chunkData = chunks[chunkIndex];
                const duplicatesBeforeChunk = foundDuplicateRows.length;
                
                const { url, method } = buildApiRequestConfig(routes.transaction.checkDuplicate, [{ param: 'entityId', value: user.entityId }]);
                
                const requestData = chunkData.map(item => ({
                    date: item.date,
                    amount: item.amount,
                    description: item.description
                }));
                
                // Make direct fetch request with authentication headers
                const token = Cookies.get('token');
                const headers: Record<string, string> = {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                };
                
                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }
                
                const response = await fetch(url, {
                    method: method,
                    headers,
                    body: JSON.stringify(requestData)
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const responseData = await response.json();
                const duplicatesInChunk = (responseData.duplicates || []) as any[];
                
                // Find matching rows in the chunk for each duplicate
                // Note: foundDuplicateRows is declared outside the chunk loop
                const processedIndices = new Set<number>();

                duplicatesInChunk.forEach((duplicate: any) => {
                    const dupDate = utilityFunctions.parseDate(duplicate.date);
                    const dupDateStr = dupDate ? utilityFunctions.formatDate(dupDate) : '';
                    const dupAmount = typeof duplicate.amount === 'string' ? parseFloat(duplicate.amount) : duplicate.amount;
                    const duplicateKey = `${dupDateStr}|${dupAmount}|${duplicate.description.toLowerCase()}`;

                    // Find one matching row in the chunk for this specific duplicate
                    const matchingIndex = chunkData.findIndex((item: any, index: number) => {
                        if (processedIndices.has(index)) return false;
                        
                        const itemDate = utilityFunctions.parseDate(item.date);
                        const itemDateStr = itemDate ? utilityFunctions.formatDate(itemDate) : '';
                        const itemAmount = typeof item.amount === 'string' ? parseFloat(item.amount) : item.amount;
                        const itemKey = `${itemDateStr}|${itemAmount}|${item.description.toLowerCase()}`;
                        return itemKey === duplicateKey;
                    });

                    if (matchingIndex !== -1) {
                        const matchingItem = chunkData[matchingIndex];
                        processedIndices.add(matchingIndex);
                        foundDuplicateRows.push({
                            id: `${matchingItem.date}|${matchingItem.amount}|${matchingItem.description}|${matchingItem.originalIndex}`,
                            date: matchingItem.date,
                            amount: String(matchingItem.amount),
                            description: matchingItem.description,
                            suggestedCategory: 'Duplicate',
                            unmatched: false,
                            checked: true,
                        });
                    }
                });

                // Add non-duplicate rows from this chunk (exclude those that were marked as duplicates)
                chunkData.forEach((item, index) => {
                    if (!processedIndices.has(index)) {
                        nonDuplicateRows.push(item.originalRow);
                    }
                });
                // Count duplicates found in this chunk
                const duplicatesInThisChunk = foundDuplicateRows.length - duplicatesBeforeChunk;
                duplicateCounts.push(duplicatesInThisChunk);
                
                // Update transactions processed count
                totalTransactionsProcessed += chunkData.length;
                
                // Update progress state
                setDuplicateCheckProgress({
                    currentChunk: chunkIndex + 1,
                    totalChunks: chunks.length,
                    processedDuplicates: totalTransactionsProcessed,
                    totalDuplicates: totalTransactionsToProcess
                });
                
                // Small delay before processing next chunk
                await new Promise(resolve => setTimeout(resolve, 200));
                
            } catch (error) {
                console.error('Error processing chunk:', chunkIndex, error);
                // Continue with next chunk even if one fails
            }
        }

        // All chunks processed
        const totalDuplicatesFound = duplicateCounts.reduce((sum, count) => sum + count, 0);
        setDuplicateCount(totalDuplicatesFound);
        setDuplicateRows(sortRowsByDateDesc(foundDuplicateRows));
        setFilteredRows(nonDuplicateRows);
        setMatchedCount(prev => ({ ...prev, duplicates: totalDuplicatesFound }));
        
        // Reset progress
        setDuplicateCheckProgress({ currentChunk: 0, totalChunks: 0, processedDuplicates: 0, totalDuplicates: 0 });
        
        // Cache the results to avoid re-running API on step navigation
        const dataHash = JSON.stringify(parsedRows).substring(0, 50);
        duplicateCheckCacheRef.current = {
            fileDataHash: dataHash,
            duplicateRows: foundDuplicateRows,
            filteredRows: nonDuplicateRows,
            duplicateCount: totalDuplicatesFound
        };
        
        isCheckingDuplicatesRef.current = false;
        setIsProcessing(false);
    }, [user, utilityFunctions, setMatchedCount]);

    const processRowsInChunks = useCallback(async (parsedRows: any[], chunkSize = 50) => {
        // Use filtered rows (non-duplicates) if duplicates have been processed -
        // step 1 is Duplicate Checking (see the step===1 branch above that sets
        // hasProcessedStep[1]); hasProcessedStep[0] was never set anywhere in
        // this file, so this always fell through to the raw, unfiltered
        // parsedRows regardless of the duplicate check's results.
        const rowsToProcess = hasProcessedStep[1] ? filteredRows : parsedRows;
        const stripNumber = (s: string) => s.replace(/[^0-9.-]/g, '');
        const toNumber = (v?: any) => {
            const n = parseFloat(stripNumber(utilityFunctions.normalize(v)));
            return Number.isFinite(n) ? n : NaN;
        };

        const totalRows = rowsToProcess.length;
        const processedRows: ProvisionRow[] = [];

        // Tracks which provisions have already been claimed by an earlier
        // imported row in this same run - without this, two similar imported
        // rows (e.g. two payments close in date/amount/description) could
        // both independently score the SAME provision as their best match
        // and both get linked to it. bulkCreateTransactions then deducts
        // each linked transaction's amount from the provision in turn,
        // decrementing it past zero into an incorrect (often negative)
        // amount for a provision that should only ever be linked once.
        // Persisted across chunks (declared outside the loop) since two
        // matching rows can easily land in different chunks.
        const claimedProvisionIds = new Set<string>();

        for (let i = 0; i < totalRows; i += chunkSize) {
            const chunk = rowsToProcess.slice(i, i + chunkSize);

            const chunkResults = chunk.map((row, chunkIndex) => {
                const imported = {
                    date: utilityFunctions.normalize(row['Date'] ?? row.date),
                    amount: utilityFunctions.normalize(row['Amount'] ?? row.amount),
                    description: utilityFunctions.normalize(row['Description'] ?? row.description),
                    category: utilityFunctions.normalize(row['Category'] ?? row.category),
                    sourceData: row.sourceData,
                };

                let matchedProvision:
                    | {
                          id: string;
                          date: string;
                          amount: number | string;
                          description: string;
                          category: string;
                      }
                    | undefined = undefined;

                if (provisions && provisions.length) {
                    const impAmountNum = toNumber(imported.amount);
                    const impDesc = imported.description?.toLowerCase();
                    const impCat = imported.category?.toLowerCase();

                    let bestScore = 0;
                    const DAY_MS = 24 * 60 * 60 * 1000;

                    for (const p of provisions) {
                        if (claimedProvisionIds.has(p.id)) continue;
                        let score = 0;

                        const pAmountNum = toNumber(p.amount);
                        const amountTolerance = provisionMatchConfig?.amountTolerance ?? 0.01;
                        if (!Number.isNaN(impAmountNum) && !Number.isNaN(pAmountNum) && Math.abs(impAmountNum - pAmountNum) < amountTolerance) {
                            score += provisionMatchConfig?.amountPoints ?? 1.25; 
                        }

                        if (imported.date && p.date) {
                            const impDate = utilityFunctions.parseDate(imported.date);
                            const pDate = utilityFunctions.parseDate(p.date);
                            const impMs = impDate?.getTime();
                            const pMs = pDate?.getTime();
                            
                            if (pMs != null && impMs != null && !isNaN(pMs) && !isNaN(impMs)) {
                                const dayDiff = Math.round(Math.abs(pMs - impMs) / DAY_MS);
                                let dateScore = 0;
                                if (dayDiff === 0) {
                                    dateScore = provisionMatchConfig?.sameDayPoints ?? 1.0;      // exact match
                                } else if (dayDiff <= 1) {
                                    dateScore = provisionMatchConfig?.within1DayPoints ?? 0.9;    // within 1 day
                                } else if (dayDiff <= 3) {
                                    dateScore = provisionMatchConfig?.within3DayPoints ?? 0.7;    // within 3 days
                                } else if (dayDiff <= 7) {
                                    dateScore = provisionMatchConfig?.within7DayPoints ?? 0.4;    // within a week
                                } else if (dayDiff <= 30) {
                                    dateScore = provisionMatchConfig?.within30DayPoints ?? 0.15;   // within a month
                                } else {
                                    dateScore = 0;      // too far apart
                                }
                                score += dateScore;
                            }
                        }
                        
                        // Token-based description matching
                        if (p?.description && impDesc) {
                            // Use originalDescription if available for more accurate matching
                            const provisionDesc = p.originalDescription || p.description;
                            const matchingTokens = countMatchingTokens(imported.description, provisionDesc);
                            if (matchingTokens > 0) {
                                // Use configurable thresholds or fallback to defaults
                                const thresholds = provisionMatchConfig?.descriptionTokenThresholds || [
                                    { minTokens: 1, score: 0.25 },
                                    { minTokens: 2, score: 0.5 },
                                    { minTokens: 3, score: 0.75 },
                                    { minTokens: 4, score: 1.0 },
                                ];
                                
                                // Find the highest applicable threshold
                                let descScore = 0;
                                for (let i = thresholds.length - 1; i >= 0; i--) {
                                    if (matchingTokens >= thresholds[i].minTokens) {
                                        descScore = thresholds[i].score;
                                        break;
                                    }
                                }
                                score += descScore;
                            }
                        }
                        
                        if (p?.category) {
                            const basePoints = provisionMatchConfig?.categoryBasePoints || 0.25;
                            score += basePoints;

                            const provCategory = findCategoryById(Number(p.category));
                            const provLabel = provCategory?.label?.toLowerCase();
                            const provValue = String(p.category).toLowerCase();
                            if (impCat && (provLabel === impCat || provValue === impCat)) {
                                const bonusPoints = provisionMatchConfig?.categoryBonusPoints || 0.75;
                                score += bonusPoints;
                            }
                        }

                        if (score > bestScore) {
                            bestScore = score;
                            matchedProvision = p;
                        }
                    }

                    const minimumThreshold = provisionMatchConfig?.minimumThreshold ?? 2;
                    if (bestScore < minimumThreshold) {
                        matchedProvision = undefined;
                    }

                    if (matchedProvision) {
                        claimedProvisionIds.add(matchedProvision.id);
                    }
                }

                const absoluteIndex = i + chunkIndex;
                return {
                    id: `${imported.date}|${imported.amount}|${imported.description}|${absoluteIndex}`,
                    imported,
                    provision: matchedProvision
                        ? {
                                id: matchedProvision.id,
                                date: utilityFunctions.normalize(matchedProvision.date),
                                amount: String(matchedProvision.amount),
                                description: utilityFunctions.normalize(matchedProvision.description),
                                category: findCategoryById(Number(matchedProvision!.category))?.label,
                            }
                        : {
                                id: '-',
                                date: '-',
                                amount: '-',
                                description: '-',
                                category: '-',
                            },
                    checked: true,
                } as ProvisionRow;
            });

            processedRows.push(...chunkResults);
            
            await new Promise(resolve => requestAnimationFrame(() => resolve(void 0)));
        }

        const provisionRowsFiltered = processedRows.filter(r => r.provision && r.provision.id !== '-');
        setMatchedCount(prev => ({ ...prev, provision: provisionRowsFiltered.length }));
        const sortedProvisionRows = sortRowsByDateDesc(provisionRowsFiltered);
        setProvisionMatchRows(sortedProvisionRows);
        setProvisionMatchRows(rows => rows.map(r => ({ ...r, checked: checkedStates[1]?.[r.id] ?? true })));
        
        setIsProcessing(false);
    }, [provisions, utilityFunctions, setMatchedCount, findCategoryById, checkedStates, filteredRows, hasProcessedStep]);

    const processCategoryRowsInChunks = useCallback(async (parsedRows: any[], chunkSize = 100) => {
        // Use filtered rows (non-duplicates) if duplicates have been processed -
        // step 1 is Duplicate Checking (see the step===1 branch above that sets
        // hasProcessedStep[1]); hasProcessedStep[0] was never set anywhere in
        // this file, so this always fell through to the raw, unfiltered
        // parsedRows regardless of the duplicate check's results.
        const rowsToProcess = hasProcessedStep[1] ? filteredRows : parsedRows;
        const totalRows = rowsToProcess.length;
        const processedRows: MatchRow[] = [];

        const approvedInPreviousSteps = new Set();
        
        // Add checked provisions from step 1 (only approved ones should be excluded from category matching)
        provisionMatchRows.filter(r => r.checked).forEach(r => {
            const key = `${r.imported?.date}|${r.imported?.amount}|${r.imported?.description}`;
            approvedInPreviousSteps.add(key);
        });

        for (let i = 0; i < totalRows; i += chunkSize) {
            const chunk = rowsToProcess.slice(i, i + chunkSize);
            
            const chunkResults = chunk.map((row: any, chunkIndex: number) => {
                const imported = {
                    date: utilityFunctions.normalize(row['Date'] ?? row.date),
                    amount: utilityFunctions.normalize(row['Amount'] ?? row.amount),
                    description: utilityFunctions.normalize(row['Description'] ?? row.description),
                    category: utilityFunctions.normalize(row['Category'] ?? row.category),
                    sourceData: row.sourceData,
                };

                const key = `${imported.date}|${imported.amount}|${imported.description}`;
                if (approvedInPreviousSteps.has(key)) {
                    return null;
                }

                const matchedCategory = findMatchedCategory(imported.category, imported.description);
                const absoluteIndex = i + chunkIndex;
                return {
                    id: `${key}|${absoluteIndex}`, // Add index to ensure unique IDs for identical rows
                    date: imported.date || '',
                    amount: imported.amount || '',
                    description: imported.description || '',
                    suggestedCategory: matchedCategory ? matchedCategory.label : '',
                    unmatched: matchedCategory ? false : true,
                    checked: true,
                    sourceData: imported.sourceData,
                } as MatchRow;
            }).filter((row): row is MatchRow => row !== null);

            processedRows.push(...chunkResults);
            
            await new Promise(resolve => requestAnimationFrame(() => resolve(void 0)));
        }

        const matchedRows = processedRows.filter(r => !r.unmatched);
        const unmatchedRows = processedRows.filter(r => r.unmatched);
        
        setMatchedCount(prev => ({ 
            ...prev, 
            category: matchedRows.length, 
            unmatched: unmatchedRows.length 
        }));
        const sortedMatchedRows = sortRowsByDateDesc(matchedRows);
        setCategoryMatchRows(sortedMatchedRows.map(r => ({ ...r, checked: checkedStates[2]?.[r.id] ?? true })));
        // Store unmatched rows for step 3 - checked by default
        const unmatchedForStep3 = sortRowsByDateDesc(unmatchedRows).map(r => ({ ...r, checked: checkedStates[3]?.[r.id] ?? true }));
        setOriginalUnmatchedRows(unmatchedForStep3);
        setCategoryUnmatchedRows(unmatchedForStep3);
        
        setIsProcessing(false);
    }, [utilityFunctions, findMatchedCategory, setMatchedCount, filteredRows, hasProcessedStep, provisionMatchRows, checkedStates]);

    // Step 3 just needs to display the unmatched rows from step 2, nothing to update
    // The categoryUnmatchedRows state already contains the correct data
    
    // Build step 3 data: combine unchecked rows from step 2 with unmatched rows
    const buildStep3Rows = useCallback(() => {
        // Get all rows from step 2 that are NOT checked (user rejected auto-match)
        const uncheckedFromStep2 = categoryMatchRows
            .filter(r => !r.checked)
            .map(r => ({
                ...r,
                suggestedCategory: '', // Clear auto-suggested category
                checked: true // Default checked for manual review
            }));
        
        // Create a Set of ALL IDs from step 2 (both checked and unchecked) to avoid duplicates
        // If a row was in step 2 at all, it shouldn't be added again from originalUnmatchedRows
        const allStep2Ids = new Set(categoryMatchRows.map(r => r.id));
        
        // Filter original unmatched rows to exclude any that were already in step 2
        const uniqueOriginalRows = originalUnmatchedRows.filter(r => !allStep2Ids.has(r.id));
        
        // Combine deduplicated rows
        const allStep3Rows = [
            ...uncheckedFromStep2,
            ...uniqueOriginalRows
        ];
        
        setCategoryUnmatchedRows(sortRowsByDateDesc(allStep3Rows));
        setMatchedCount(prev => ({ ...prev, unmatched: allStep3Rows.length }));
    }, [categoryMatchRows, originalUnmatchedRows, checkedStates]);

    // Check if user has Basiq when Basiq import type is selected
    React.useEffect(() => {
        if (importType === 'basiq' && userHasBasiq === null) {
            const { url, method } = buildApiRequestConfig(routes.basiq.checkUser, [{ param: 'userGuid', value: user?.guid }]);
            setCheckBasiqUser({ url, method });
        }
    }, [importType, user?.guid, userHasBasiq]);

    // Handle check user response
    React.useEffect(() => {
        const { data, isLoading, error } = checkBasiqUser;
        
        if (!isLoading && data) {
            // Determine if user has Basiq based on response
            const hasBasiq = data.hasIntegration === true;
            setUserHasBasiq(hasBasiq);
            
            if (hasBasiq) {
                // Store the Basiq user ID for transaction fetching
                const basiqGuid = data.data?.basiqGuid;
                setBasiqUserId(basiqGuid);
                
                // Set default date range (last 90 days)
                const toDate = new Date();
                const fromDate = new Date();
                fromDate.setDate(fromDate.getDate() - 90);
                setBasiqDateRange({
                    from: utilityFunctions.formatDate(fromDate),
                    to: utilityFunctions.formatDate(toDate)
                });
                // UI will render date picker based on userHasBasiq state
            } else {
                // UI will render connect form based on userHasBasiq state
            }
        }
        
        if (error) {
            showError(error?.toString?.() ?? 'Failed to check Basiq connection');
            setImportType(null);
            setUserHasBasiq(null);
        }
    }, [checkBasiqUser, utilityFunctions, showError]);

    // Handle Basiq user creation response
    React.useEffect(() => {
        const { data, isLoading, error } = createBasiqUser;
        
        if (!isLoading && data) {
            // User successfully created/connected to Basiq
            setUserHasBasiq(true);
            
            // Store the Basiq user ID for transaction fetching
            const basiqGuid = data.data?.basiqGuid || data.basiqGuid;
            setBasiqUserId(basiqGuid);
            
            // Set default date range (last 90 days)
            const toDate = new Date();
            const fromDate = new Date();
            fromDate.setDate(fromDate.getDate() - 90);
            setBasiqDateRange({
                from: utilityFunctions.formatDate(fromDate),
                to: utilityFunctions.formatDate(toDate)
            });
            
            // UI will render date picker based on userHasBasiq state
        }
        
        if (error) {
            showError(error?.toString?.() ?? 'Failed to connect to Basiq');
        }
    }, [createBasiqUser, utilityFunctions, showError]);

    // Handle Basiq connect user response (redirect to consent UI)
    React.useEffect(() => {
        const { data, isLoading, error } = connectBasiqUser;
        
        if (!isLoading && data) {
            // Backend returns consentUrl to open in new window
            if (data.consentUrl) {
                window.open(data.consentUrl, '_blank');
                setBasiqConsentCompleted(true);
            }
        }
        
        if (error) {
            showError(error?.toString?.() ?? 'Failed to open Basiq Consent UI');
        }
    }, [connectBasiqUser, showError]);



    // Handle Basiq transaction response
    React.useEffect(() => {
        const { data, isLoading, error } = fetchBasiqTransactions;
        
        if (!isLoading && data) {
            const transactions = Array.isArray(data) ? data : data.transactions || [];

            // Transform Basiq data to match CSV format
            const transformedRows = transactions.map((txn: any) => ({
                Date: utilityFunctions.formatDate(new Date(txn.date || txn.transactionDate)),
                Amount: String(txn.amount || ''),
                Description: txn.description || txn.memo || '',
                Category: txn.category || ''
            }));

            if (transformedRows.length === 0) {
                showError('No transactions found for the selected date range');
                setIsProcessing(false);
                return;
            }

            // Create virtual file object for Basiq
            const basiqFile = new File([], `basiq-import-${Date.now()}`);
            
            // Call onFileChange to trigger the workflow
            onFileChange?.({
                file: basiqFile,
                parsedRows: transformedRows,
                failedRows: [],
                result: {
                    successfulRows: transformedRows.length,
                    failedRows: 0,
                    totalRows: transformedRows.length
                },
                headers: ['Date', 'Amount', 'Description', 'Category']
            } as FileDetails);

            // Set local file state so business type modal can be triggered
            setFile(basiqFile);

            // Mark as just selected to trigger next step
            setIsFileJustSelected(true);
            
            // Move to step 1 (duplicate checking)
            setStep(1);
            
            setIsProcessing(false);
        }
        
        if (error) {
            showError(error?.toString?.() ?? 'Failed to fetch Basiq transactions');
            setIsProcessing(false);
        }
    }, [fetchBasiqTransactions, utilityFunctions, onFileChange, showError]);

    React.useEffect(() => {
        const { parsedRows } = fileDetails || {};
        if (parsedRows && step === 1 && !isCheckingDuplicatesRef.current && !hasProcessedStep[1]) {
            // Create a hash of the parsed rows to check if it's the same file
            const dataHash = JSON.stringify(parsedRows).substring(0, 50); // Simple hash of first 50 chars
            
            // Check if results are cached for this file
            if (duplicateCheckCacheRef.current && duplicateCheckCacheRef.current.fileDataHash === dataHash) {
                // Use cached results instead of calling API again
                setDuplicateRows(duplicateCheckCacheRef.current.duplicateRows);
                setFilteredRows(duplicateCheckCacheRef.current.filteredRows);
                setDuplicateCount(duplicateCheckCacheRef.current.duplicateCount);
                setHasProcessedStep(prev => ({ ...prev, 1: true }));
                setIsProcessing(false);
            } else {
                // New file data, call API
                setIsProcessing(true);
                setHasProcessedStep(prev => ({ ...prev, 1: true }));
                processDuplicatesInChunks(parsedRows);
            }
        } else if (parsedRows && categories && step === 2 && !hasProcessedStep[2]) {
            setIsProcessing(true);
            setHasProcessedStep(prev => ({ ...prev, 2: true }));
            processRowsInChunks(parsedRows);
        } else if (!parsedRows) {
            setProvisionMatchRows([]);
            setIsProcessing(false);
        }
    }, [fileDetails, categories, processDuplicatesInChunks, processRowsInChunks, step]);

    React.useEffect(() => {
        const { data, isLoading, error } = createImport;
        if (!isLoading && data) {
            const newImportId = data.importEvent.id;
            setImportId(newImportId);
            setRealTransactionCount(data.realTransactionCount || null);
        }
        if (error) {
            showError('Failed to initialize import. Please try again.');
        }
    }, [createImport, showError]);

    const createImportEvent = React.useCallback(() => {
        const { url, method } = buildApiRequestConfig(routes.transaction.createImportEvent);
        setCreateImport({
            method,
            url,
            body: {
                entityId: user?.entityId,
                moduleName: 'transactions',
                fileName: fileDetails?.file?.name || 'imported_file.csv'
            }
        });

    }, [user?.entityId, fileDetails?.file?.name]);

    React.useEffect(() => {
        if (open && fileDetails && !importId && !isCreatingImportEvent) {
            setIsCreatingImportEvent(true);
            createImportEvent();
        }
    }, [open, fileDetails, importId, isCreatingImportEvent, createImportEvent]);

    React.useEffect(() => {
        const { parsedRows } = fileDetails || {};
        if (parsedRows && categories && step === 3 && !hasProcessedStep[3]) {
            setIsProcessing(true);
            setHasProcessedStep(prev => ({ ...prev, 3: true }));
            processCategoryRowsInChunks(parsedRows).finally(() => {
                setIsProcessing(false);
            });
        }
    }, [fileDetails, categories, processCategoryRowsInChunks, step]);

    // Update step based on progress modal state
    React.useEffect(() => {
        if (progressModal.open && (progressModal.status === 'success' || progressModal.status === 'error')) {
            setStep(6); // Final results step
            if (progressModal.status === 'success') {
                // Convert approvedForImport to display format
                setImportedEntries(approvedForImport.map((entry, index) => ({
                    id: (index + 1).toString(),
                    date: entry.date || '',
                    amount: entry.amount != null ? String(entry.amount) : '',
                    description: entry.description || '',
                    category: findCategoryById(entry.categoryId)?.label ?? 'Uncategorized',
                    source: entry.source || 'unknown'
                })));
            }
        }
    }, [progressModal, approvedForImport, findCategoryById]);

    const allChecked = () => {
        switch(step) {
            case 0:
            case 1:
            case 5:
                return true; // Steps 0, 1, 5 have no checkboxes
            case 2:
                return provisionMatchRows.length > 0 && provisionMatchRows.every(r => r.checked);
            case 3:
                return categoryMatchRows.length > 0 && categoryMatchRows.every(r => r.checked);
            case 4:
                return categoryUnmatchedRows.length > 0 && categoryUnmatchedRows.every(r => r.checked);
            default:
                return false;
        }
    };
    const handleCheck = (id: string, checked: boolean) => {
        setCheckedStates(prev => ({
            ...prev,
            [step]: {
                ...prev[step],
                [id]: checked
            }
        }));
        switch(step) {
            case 0:
            case 1:
            case 5:
                // Steps 0, 1, 5 have no checkboxes
                break;
            case 2:
                setProvisionMatchRows(rows => rows.map(r => r.id === id ? { ...r, checked } : r));
                break;
            case 3:
                setCategoryMatchRows(rows => rows.map(r => r.id === id ? { ...r, checked } : r));
                break;
            case 4:
                setCategoryUnmatchedRows(rows => rows.map(r => r.id === id ? { ...r, checked } : r));
                break;
        }
    };
    const handleCheckAll = (checked: boolean) => {
        switch(step) {
            case 0:
            case 1:
            case 5:
                // Steps 0, 1, 5 have no checkboxes
                break;
            case 2:
                const updatedProvisions = provisionMatchRows.map(r => ({ ...r, checked }));
                setProvisionMatchRows(updatedProvisions);
                setCheckedStates(prev => { 
                    const newState = { ...prev };
                    newState[2] = {} as { [id: string]: boolean };
                    updatedProvisions.forEach(r => newState[2][r.id] = checked); 
                    return newState; 
                });
                break;
            case 3:
                const updatedCategories = categoryMatchRows.map(r => ({ ...r, checked }));
                setCategoryMatchRows(updatedCategories);
                setCheckedStates(prev => { 
                    const newState = { ...prev };
                    newState[3] = {} as { [id: string]: boolean };
                    updatedCategories.forEach(r => newState[3][r.id] = checked); 
                    return newState; 
                });
                break;
            case 4:
                const updatedUnmatched = categoryUnmatchedRows.map(r => ({ ...r, checked }));
                setCategoryUnmatchedRows(updatedUnmatched);
                setCheckedStates(prev => { 
                    const newState = { ...prev };
                    newState[4] = {} as { [id: string]: boolean };
                    updatedUnmatched.forEach(r => newState[4][r.id] = checked); 
                    return newState; 
                });
                break;
        }
    };
    
    // Paginated data slices for steps 2, 3, and 4
    const paginatedCategoryMatchRows = useMemo(() => {
        const startIndex = (page[3] - 1) * rowsPerPage;
        return categoryMatchRows.slice(startIndex, startIndex + rowsPerPage);
    }, [categoryMatchRows, page, rowsPerPage]);



    const paginatedCategoryUnmatchedRows = useMemo(() => {
        const startIndex = (page[4] - 1) * rowsPerPage;
        return categoryUnmatchedRows.slice(startIndex, startIndex + rowsPerPage);
    }, [categoryUnmatchedRows, page, rowsPerPage]);

    const paginatedApprovedForImport = useMemo(() => {
        const startIndex = (page[5] - 1) * rowsPerPage;
        return approvedForImport.slice(startIndex, startIndex + rowsPerPage);
    }, [approvedForImport, page, rowsPerPage]);

    const handlePageChange = (step: number, newPage: number) => {
        setPage(prev => ({ ...prev, [step]: newPage }));
    };

    const formatFileSize = (size: number): string => {
        const kb = size / 1024;
        if (kb < 1024) return `${Math.ceil(kb)} KB`;
        return `${Math.ceil(kb / 1024)} MB`;
    };

    const validateFileSelection = (): boolean => {
        const newErrors: { bank?: string, file?: string } = {};
        if (!selectedBank) {
            newErrors.bank = 'Bank is required';
        }
        if (!file) {
            newErrors.file = 'File is required';
        } else {
            if (file.type !== 'text/csv' && file.type !== 'text/tab-separated-values') {
                newErrors.file = 'Invalid file type. Only CSV and TSV files are allowed.';
            }
            if (file.size > 50 * 1024 * 1024) {
                newErrors.file = 'File size exceeds 50MB limit';
            }
        }
        setFileErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // SelectFileStep render component
    const SelectFileStepRender = (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', py: 1 }}>
            {!enableBasiqImport ? (
                // When Basiq is disabled, skip type selection and go directly to file upload
                // File import UI
                <Box maxWidth="sm" width="100%">
                    <AddBankAccountPopup
                        open={showAddBankModal}
                        onClose={() => {onClose(); setShowAddBankModal(false); setSelectedBankAccount(null); setStep(0);}}
                        forImport={true}
                        onCreate={(accountData) => {
                            const { url, method } = buildApiRequestConfig(routes.bankAccounts.create);
                            setCreateBankAccount({ url, method, body: {
                                ...accountData, entityId: user?.entityId, accountName: accountData.name
                            }});
                        }}
                    />
                    <Box mb={1} width="100%">
                        <Typography fontSize={14} color='var(--color-text-muted)' mb={0.5}>
                            Select Bank <span style={{ color: '#ff3b3b' }}>*</span>
                        </Typography>
                        <RadioGroup
                            value={selectedBank || ''}
                            onChange={(e) => {
                                const value = e.target.value;
                                setSelectedBank(value);
                                const selectedBankData = fetchedBankData.find(b => String(b.id) === value);
                                if (selectedBankData) {
                                    setSelectedBankAccount(selectedBankData);
                                    try {
                                        const importConfig = JSON.parse(selectedBankData.importConfig) as ImportConfig;
                                        setContextImportConfig(importConfig);
                                    } catch (e) {
                                        console.error('Failed to parse importConfig:', e);
                                    }
                                }
                                onBankChange?.(typeof value === 'string' ? parseInt(value, 10) : value);
                            }}
                        >
                            {bankOptions.map(option => (
                                <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    control={<Radio />}
                                    label={option.label}
                                />
                            ))}
                        </RadioGroup>
                        {fileErrors.bank && <Typography color="error" fontSize={12} mt={0.5}>{fileErrors.bank}</Typography>}
                    </Box>
                    <Box width="100%">
                        <Typography fontSize={14} color='var(--color-text-muted)' mb={1}>
                            Choose a File <span style={{ color: '#ff3b3b' }}>*</span>
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                background: 'var(--color-container-background)',
                                borderRadius: 1,
                                border: `1px solid ${fileErrors.file ? '#e31616ff' : '#e0e0e0'}`,
                                px: 2,
                                py: 1.2,
                            }}
                        >
                            <Typography
                                sx={{ 
                                    flex: 1, 
                                    color: file ? 'inherit' : '#b0b0b0', 
                                    fontSize: 15, 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    whiteSpace: 'nowrap' 
                                }}
                            >
                                {file ? `${file.name} (${formatFileSize(file.size)})` : 'CSV, TSV formats, up to 50MB'}
                            </Typography>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,.tsv,.txt"
                                style={{ display: 'none' }}
                                onChange={e => {
                                    const selectedFile = e.target.files?.[0] || null;
                                    setFile(selectedFile);
                                    if (e.target) {
                                        e.target.value = '';
                                    }
                                }}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                sx={{ ml: 2, borderRadius: 2, minWidth: 80, textTransform: 'none', fontWeight: 600 }}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Upload
                            </Button>
                        </Box>
                        {selectedImportConfig && (file && fileErrors.file) && (
                            <Box my={0.5} width="100%">
                                <Box 
                                    sx={{
                                        backgroundColor: fileErrors.file ? '#fff5f5' : 'var(--color-container-background)',
                                        border: fileErrors.file ? '1px solid #ffcccb' : '1px solid #e0e0e0',
                                        borderRadius: 1.5,
                                        p: 2
                                    }}
                                >
                                    <Typography fontSize={12} fontWeight={600} color={fileErrors.file ? '#dc1111ff' : 'var(--color-text-muted)'} mb={0.5}>
                                        Expected Columns:
                                    </Typography>
                                    <Typography fontSize={11} color="var(--color-text-muted)" mb={0.5} sx={{ fontWeight: 500 }}>
                                        {selectedImportConfig.dateColumn}, {selectedImportConfig.descriptionColumn}
                                        {selectedImportConfig.amountType === 'separate' 
                                            ? `, ${selectedImportConfig.debitColumn}, ${selectedImportConfig.creditColumn}` 
                                            : `, ${selectedImportConfig.amountColumn}`}
                                    </Typography>
                                    {fileErrors.file && (
                                        <>
                                            <Typography fontSize={12} fontWeight={600} color={fileErrors.file ? '#dc1111ff' : 'var(--color-text-muted)'} mb={0.5}>
                                                Columns Found in Uploaded File:
                                            </Typography>
                                            <Typography fontSize={11} color="#dc1111ff" sx={{ whiteSpace: 'pre-wrap' }}>
                                                {fileErrors.file}
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            ) : importType === null ? (
                // Type selection (when Basiq is enabled)
                <Box sx={{ display: 'flex', gap: 3, flexDirection: 'column', width: '100%', maxWidth: 'sm' }}>
                    <Typography variant="h6" sx={{ textAlign: 'center', mb: 1 }}>
                        How would you like to import?
                    </Typography>
                    <Box display="flex" gap={3}>
                        <Box
                            sx={{
                                p: 3,
                                flex: 1,
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                cursor: 'pointer',
                                textAlign: 'center',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    border: '1px solid #00b59e',
                                    boxShadow: '0 2px 8px rgba(0, 181, 158, 0.15)',
                                    backgroundColor: '#f5fffe'
                                }
                            }}
                            onClick={() => setImportType('file')}
                        >
                            <Box sx={{ fontSize: 48, mb: 1 }}>📄</Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Upload a File (CSV)
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                p: 3,
                                flex: 1,
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                cursor: 'pointer',
                                textAlign: 'center',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    border: '1px solid #00b59e',
                                    boxShadow: '0 2px 8px rgba(0, 181, 158, 0.15)',
                                    backgroundColor: '#f5fffe'
                                }
                            }}
                            onClick={() => setImportType('basiq')}
                        >
                            <Box sx={{ fontSize: 48, mb: 1 }}>🏦</Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                From Bank (Via Basiq)
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            ) : importType === 'file' ? (
                // File import UI
                <Box maxWidth="sm" width="100%">
                    <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                        <Button 
                            variant="text" 
                            size="small"
                            sx={{ textTransform: 'none', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: 0.5 }}
                            onClick={() => {
                                setImportType(null);
                                setFile(null);
                                setFileErrors({});
                                setSelectedBank('');
                            }}
                        >
                            <ArrowLeft size={16}/> Back
                        </Button>
                    </Box>
                    <AddBankAccountPopup
                        open={showAddBankModal}
                        onClose={() => {onClose(); setShowAddBankModal(false); setSelectedBankAccount(null); setStep(0);}}
                        forImport={true}
                        onCreate={(accountData) => {
                            const { url, method } = buildApiRequestConfig(routes.bankAccounts.create);
                            setCreateBankAccount({ url, method, body: {
                                ...accountData, entityId: user?.entityId, accountName: accountData.name
                            }});
                        }}
                    />
                    <Box mb={1} width="100%">
                        <Typography fontSize={14} color='var(--color-text-muted)' mb={0.5}>
                            Select Bank <span style={{ color: '#ff3b3b' }}>*</span>
                        </Typography>
                        <RadioGroup
                            value={selectedBank || ''}
                            onChange={(e) => {
                                const value = e.target.value;
                                setSelectedBank(value);
                                const selectedBankData = fetchedBankData.find(b => String(b.id) === value);
                                if (selectedBankData) {
                                    setSelectedBankAccount(selectedBankData);
                                    try {
                                        const importConfig = JSON.parse(selectedBankData.importConfig) as ImportConfig;
                                        setContextImportConfig(importConfig);
                                    } catch (e) {
                                        console.error('Failed to parse importConfig:', e);
                                    }
                                }
                                onBankChange?.(typeof value === 'string' ? parseInt(value, 10) : value);
                            }}
                        >
                            {bankOptions.map(option => (
                                <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    control={<Radio />}
                                    label={option.label}
                                />
                            ))}
                        </RadioGroup>
                        {fileErrors.bank && <Typography color="error" fontSize={12} mt={0.5}>{fileErrors.bank}</Typography>}
                    </Box>
                    <Box width="100%">
                        <Typography fontSize={14} color='var(--color-text-muted)' mb={1}>
                            Choose a File <span style={{ color: '#ff3b3b' }}>*</span>
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                background: 'var(--color-container-background)',
                                borderRadius: 1,
                                border: `1px solid ${fileErrors.file ? '#e31616ff' : '#e0e0e0'}`,
                                px: 2,
                                py: 1.2,
                            }}
                        >
                            <Typography
                                sx={{ 
                                    flex: 1, 
                                    color: file ? 'inherit' : '#b0b0b0', 
                                    fontSize: 15, 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    whiteSpace: 'nowrap' 
                                }}
                            >
                                {file ? `${file.name} (${formatFileSize(file.size)})` : 'CSV, TSV formats, up to 50MB'}
                            </Typography>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,.tsv,.txt"
                                style={{ display: 'none' }}
                                onChange={e => {
                                    const selectedFile = e.target.files?.[0] || null;
                                    setFile(selectedFile);
                                    if (e.target) {
                                        e.target.value = '';
                                    }
                                }}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                sx={{ ml: 2, borderRadius: 2, minWidth: 80, textTransform: 'none', fontWeight: 600 }}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Upload
                            </Button>
                        </Box>
                        {selectedImportConfig && (file && fileErrors.file) && (
                            <Box my={0.5} width="100%">
                                <Box 
                                    sx={{
                                        backgroundColor: fileErrors.file ? '#fff5f5' : 'var(--color-container-background)',
                                        border: fileErrors.file ? '1px solid #ffcccb' : '1px solid #e0e0e0',
                                        borderRadius: 1.5,
                                        p: 2
                                    }}
                                >
                                    <Typography fontSize={12} fontWeight={600} color={fileErrors.file ? '#dc1111ff' : 'var(--color-text-muted)'} mb={0.5}>
                                        Expected Columns:
                                    </Typography>
                                    <Typography fontSize={11} color="var(--color-text-muted)" mb={0.5} sx={{ fontWeight: 500 }}>
                                        {selectedImportConfig.dateColumn}, {selectedImportConfig.descriptionColumn}
                                        {selectedImportConfig.amountType === 'separate' 
                                            ? `, ${selectedImportConfig.debitColumn}, ${selectedImportConfig.creditColumn}` 
                                            : `, ${selectedImportConfig.amountColumn}`}
                                    </Typography>
                                    {fileErrors.file && (
                                        <>
                                            <Typography fontSize={12} fontWeight={600} color={fileErrors.file ? '#dc1111ff' : 'var(--color-text-muted)'} mb={0.5}>
                                                Columns Found in Uploaded File:
                                            </Typography>
                                            <Typography fontSize={11} color="#dc1111ff" sx={{ whiteSpace: 'pre-wrap' }}>
                                                {fileErrors.file}
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            ) : importType === 'basiq' ? (
                // Basiq import UI
                <Box maxWidth="sm" width="100%">
                    <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                        <Button 
                            variant="text" 
                            size="small"
                            sx={{ textTransform: 'none', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: 0.5 }}
                            onClick={() => {
                                setImportType(null);
                                setUserHasBasiq(null);
                                setBasiqDateRange({ from: '', to: '' });
                                setBasiqUserId(null);
                                setBasiqConsentCompleted(false);
                            }}
                        >
                            <ArrowLeft size={16}/> Back
                        </Button>
                    </Box>
                    {userHasBasiq === null ? (
                        // Checking connection status
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress size={48} sx={{ color: 'var(--color-primary)', mb: 2 }} />
                            <Typography color="textSecondary">Checking Basiq connection...</Typography>
                        </Box>
                    ) : userHasBasiq === false ? (
                        // User doesn't have Basiq - show connect form
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                Connect to Basiq
                            </Typography>
                            <Typography color="textSecondary" sx={{ mb: 3 }}>
                                Create a Basiq account to securely connect your bank account and import transactions.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                <Button 
                                    variant="outlined" 
                                    onClick={() => {
                                        setImportType(null);
                                        setUserHasBasiq(null);
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    variant="contained" 
                                    color="primary"
                                    onClick={() => {
                                        const { url, method } = buildApiRequestConfig(routes.basiq.createUser);
                                        setCreateBasiqUser({
                                            url,
                                            method,
                                            body: {
                                                userGuid: user?.guid,
                                                email: user?.email
                                            }
                                        });
                                    }}
                                    disabled={isProcessing}
                                >
                                    {isProcessing ? <CircularProgress size={20} /> : 'Connect to Basiq'}
                                </Button>
                            </Box>
                        </Box>
                    ) : userHasBasiq === true ? (
                        // User has Basiq - show date range + consent button
                        <Box>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
                                {basiqConsentCompleted ? 'Select Date Range for Import' : 'Connect Bank & Select Date Range'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'center' }}>
                                <DateInput
                                    label="From Date"
                                    value={basiqDateRange.from}
                                    onChange={(value) => setBasiqDateRange(prev => ({ ...prev, from: value }))}
                                />
                                <DateInput
                                    label="To Date"
                                    value={basiqDateRange.to}
                                    onChange={(value) => setBasiqDateRange(prev => ({ ...prev, to: value }))}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                <Button 
                                    variant="contained" 
                                    color="primary"
                                    sx={{ color: 'white ' }}
                                    onClick={() => {
                                        if (basiqUserId) {
                                            const { url, method } = buildApiRequestConfig(routes.basiq.connectUser, [
                                                { param: 'basiqUserId', value: basiqUserId },
                                                { param: 'returnUrl', value: window.location.href }
                                            ]);
                                            setConnectBasiqUser({
                                                url,
                                                method
                                            });
                                        }
                                    }}
                                >
                                    Connect Bank
                                </Button>
                                <Button 
                                    variant="contained" 
                                    color="primary"
                                    sx={{ color: 'white ' }}
                                    disabled={!basiqDateRange.from || !basiqDateRange.to || isProcessing}
                                    onClick={() => {
                                        setIsProcessing(true);
                                        const { url, method } = buildApiRequestConfig(routes.basiq.getTransactions);
                                        setFetchBasiqTransactions({
                                            url,
                                            method,
                                            body: {
                                                userId: basiqUserId,
                                                from: basiqDateRange.from,
                                                to: basiqDateRange.to
                                            }
                                        });
                                    }}
                                >
                                    {isProcessing ? <CircularProgress size={20} /> : 'Pull Transactions'}
                                </Button>
                            </Box>
                        </Box>
                    ) : null}
                </Box>
            ) : null}
        </Box>
    );
    
    const tableRender = [
        // Step 0: File selection
        SelectFileStepRender,
        // Step 1: Duplicate checking
        <Box>
            {isProcessing ? (
                <Box 
                    sx={{ 
                        color: 'var(--color-primary)',
                        padding: 4,
                        textAlign: 'center',
                        minHeight: 75,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'var(--color-container-background)'
                    }}
                >
                    <Player
                        autoplay
                        loop
                        animationData={searchingAnimation}
                        style={{ height: 160, width: 160 }}
                    />
                    <Box sx={{ width: '80%', maxWidth: 400, mb: 2 }}>
                        <Box 
                            sx={{ 
                                height: 8, 
                                borderRadius: 4, 
                                background: '#e0e0e0',
                                overflow: 'hidden'
                            }}
                        >
                            <Box 
                                sx={{ 
                                    height: '100%', 
                                    background: 'var(--color-primary)',
                                    width: `${duplicateCheckProgress.totalDuplicates > 0 ? (duplicateCheckProgress.processedDuplicates / duplicateCheckProgress.totalDuplicates) * 100 : 0}%`,
                                    transition: 'width 0.3s ease'
                                }}
                            />
                        </Box>
                    </Box>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={18} sx={{ color: 'var(--color-primary)' }} />
                            <Typography variant="body2" color="text.secondary">
                                Checking for duplicates...
                            </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            {duplicateCheckProgress.processedDuplicates} / {duplicateCheckProgress.totalDuplicates} transactions analyzed
                        </Typography>
                    </Box>
                </Box>
            ) : (
                <Box 
                    sx={{ 
                        color: 'var(--color-primary)',
                        textAlign: 'center',
                        minHeight: 75,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'var(--color-container-background)'
                    }}
                >
                    { !(duplicateCount > 0) && (
                        <Player
                            autoplay
                            loop
                            animationData={okAnimation}
                            style={{ height: 160, width: 160 }}
                        />
                    )}
                    <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic', color: duplicateCount > 0 ? '#ff9800' : 'var(--color-primary)', mb: duplicateCount > 0 ? 0 : 2 }}>
                        {duplicateCount > 0 ? 'Duplicate entry(s) has been found' : 'No duplicates found'}
                    </Typography>
                    {duplicateCount > 0 && (
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            {duplicateCount} {duplicateCount === 1 ? 'entry' : 'entries'} were removed because they are duplicates in the same file or already exist in the system.
                        </Typography>
                    )}
                </Box>
            )}
            {!isProcessing && duplicateRows.length > 0 && (
                <Box position="relative">
                    <TableContainer sx={{ maxHeight: 340, background: 'var(--color-container-background)' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {duplicateRows.map((row, index) => (
                                    <TableRow key={row.id} sx={{ background: index % 2 === 0 ? 'var(--color-container-background)' : '#fff' }}>
                                        <TableCell>{formatDate(row.date)}</TableCell>
                                        <TableCell>{formatAmount?.(Number(row.amount))}</TableCell>
                                        <TableCell>{row.description}</TableCell>
                                        <TableCell sx={{ color: '#d32f2f', fontWeight: 600 }}>Removed</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
        </Box>,
        <ProvisionMatchTable
            rows={provisionMatchRows}
            onCheck={handleCheck}
            onCheckAll={handleCheckAll}
            allChecked={allChecked()}
        />,
        <Box>
            <MatchTable
                rows={paginatedCategoryMatchRows}
                onCheck={handleCheck}
                allChecked={allChecked()}
                canEditCategories={false}
                placeholderText={categories.length === 0 ? "No categories available for matching" : "No matching categories found"}
                startIndex={(page[3] - 1) * rowsPerPage}
            />
        </Box>,
        <Box>
            <MatchTable
                rows={paginatedCategoryUnmatchedRows}
                onCheck={handleCheck}
                allChecked={allChecked()}
                canEditCategories={true}
                categories={categories}
                onChangeCategory={(id, category) => {
                    setCategoryUnmatchedRows(rows => rows.map(r => r.id === id ? { ...r, suggestedCategory: category } : r));
                }}
                placeholderText={categories.length === 0 ? "No categories available. Create categories first to auto-match transactions." : "No unmatched transactions found"}
                startIndex={(page[4] - 1) * rowsPerPage}
            />
        </Box>,
        // Step 4: Final review (display-only, no checkboxes, no editable categories)
        <Box>
            {approvedForImport.length === 0 ? (
                <Box 
                    sx={{ 
                        color: '#B8860B',
                        border: '1px solid #FFD700',
                        borderRadius: 2,
                        padding: 4,
                        textAlign: 'center',
                        minHeight: 75,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'var(--color-container-background)'
                    }}
                >
                    <Player
                        autoplay
                        loop
                        animationData={searchAnimation}
                        style={{ height: 160, width: 160 }}
                    />
                    <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic' }}>
                        No approved transactions yet
                    </Typography>
                </Box>
            ) : (
                <>
                    <TableContainer sx={{ maxHeight: 300, background: 'var(--color-container-background)' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedApprovedForImport.map((row, index) => {
                                    const absoluteIndex = (page[5] - 1) * rowsPerPage + index;
                                    return (
                                        <TableRow key={`review-${index}`} sx={{ background: absoluteIndex % 2 === 0 ? 'var(--color-container-background)' : '#fff' }}>
                                            <TableCell>{formatDate(utilityFunctions.parseDate(row.date) || new Date())}</TableCell>
                                            <TableCell>{formatAmount?.(Number(row.amount))}</TableCell>
                                            <TableCell>{row.description}</TableCell>
                                            <TableCell>{findCategoryById(row.categoryId)?.label ?? 'Uncategorized'}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>,
        // Step 5: Final results
        progressModal.status === 'processing' ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6} gap={3}>
                <CircularProgress size={48} sx={{ color: 'var(--color-primary)' }} />
                <Box sx={{ width: '80%', maxWidth: 400 }}>
                    <LinearProgress 
                        variant="determinate" 
                        value={(progressModal.currentChunk / progressModal.totalChunks) * 100} 
                        sx={{ height: 8, borderRadius: 4 }}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                    Importing transactions... {progressModal.currentChunk} of {progressModal.totalChunks}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Processed: {progressModal.processedTransactions} / {progressModal.totalTransactions} transactions
                </Typography>
            </Box>
        ) : progressModal.status === 'success' ? (
            <Box position="relative">
                <TableContainer sx={{ maxHeight: 300, background: 'var(--color-container-background)' }}>
                    {importedEntries.length === 0 ? (
                        <Box 
                            sx={{ 
                                border: '1px solid #FFD700',
                                borderRadius: 2,
                                backgroundColor: 'rgba(255, 217, 0, 0.13)',
                                color: '#B8860B',
                                padding: 4,
                                textAlign: 'center',
                                minHeight: 75,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic' }}>
                                No transactions were imported
                            </Typography>
                        </Box>
                    ) : (
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {importedEntries.map((entry, index) => (
                                    <TableRow key={entry.id} sx={{ background: index % 2 === 0 ? 'var(--color-container-background)' : '#fff' }}>
                                        <TableCell>{formatDate(entry.date)}</TableCell>
                                        <TableCell>{formatAmount?.(Number(entry.amount))}</TableCell>
                                        <TableCell>{entry.description}</TableCell>
                                        <TableCell>{entry.category}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </TableContainer>
            </Box>
        ) : (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6}>
                <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                    Import Failed
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                    There was an error importing your transactions. Please try again.
                </Typography>
            </Box>
        ),
        // Step 6: Import Results
        progressModal.status === 'success' ? (
            <Box position="relative">
                <TableContainer sx={{ maxHeight: 340, background: 'var(--color-container-background)' }}>
                    {importedEntries.length === 0 ? (
                        <Box 
                            sx={{ 
                                border: '1px solid #FFD700',
                                borderRadius: 2,
                                backgroundColor: 'rgba(255, 217, 0, 0.13)',
                                color: '#B8860B',
                                padding: 4,
                                textAlign: 'center',
                                minHeight: 75,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic' }}>
                                No transactions were imported
                            </Typography>
                        </Box>
                    ) : (
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {importedEntries.map((entry, index) => (
                                    <TableRow key={entry.id} sx={{ background: index % 2 === 0 ? 'var(--color-container-background)' : '#fff' }}>
                                        <TableCell>{formatDate(entry.date)}</TableCell>
                                        <TableCell>{formatAmount?.(Number(entry.amount))}</TableCell>
                                        <TableCell>{entry.description}</TableCell>
                                        <TableCell>{entry.category}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </TableContainer>
            </Box>
        ) : (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6}>
                <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                    Import Failed
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                    There was an error importing your transactions. Please try again.
                </Typography>
            </Box>
        )
    ];

    return (
        <Dialog
            open={open}
            maxWidth={isMobile ? false : "xl"}
            fullWidth={isMobile}
            {...(isMobile ? { TransitionComponent: SlideUpTransition } : {})}
            sx={isMobile ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
            PaperProps={{
                sx: isMobile
                    ? { m: 0, width: '100%', maxWidth: '100%', height: '90vh', maxHeight: '90vh', borderTopLeftRadius: 16, borderTopRightRadius: 16, borderBottomLeftRadius: 0, borderBottomRightRadius: 0, p: 0, display: 'flex', flexDirection: 'column' }
                    : { borderRadius: 4, p: 0, height: 550, display: 'flex', flexDirection: 'column' }
            }}
        >
            <BusinessTypeDetectionModal
                open={businessTypeModal}
                realTransactionCount={realTransactionCount}
                importTransactionCount={fileDetails?.parsedRows?.length || 0}
                transactions={fileDetails?.parsedRows?.map(row => ({
                    description: row.description || '',
                    amount: parseFloat(String(row.amount || 0))
                })) || []}
                onCancel={() => {onClose(); setBusinessTypeModal(false); setFile(null); setSelectedBankAccount(null); setStep(0);} }
                onConfirm={() => {
                    // Close the business type modal
                    setBusinessTypeModal(false);
                    // Trigger a refetch of categories to load newly created ones
                    setCategoryRefreshTrigger(prev => prev + 1);
                }}
            />
            <DialogContent sx={{ p: 0, pt: 3, pb: 0, px: isMobile ? 2 : 4, width: isMobile ? '100%' : 1200, overflowX: 'hidden', overflowY: 'auto', position: 'relative', flex: 1 }}>
                <IconButton
                    onClick={() => {
                        onClose(); 
                    }}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 1,
                        '&:hover': {
                            backgroundColor: '#e0e0e0',
                        },
                        width: 32,
                        height: 32,
                    }}
                >
                    <X size={18} />
                </IconButton>
                {step === 6 && progressModal.status === 'success' &&
                    <Typography textAlign="center" fontSize={14} fontWeight={500} mb={2}>
                        A total of <span style={{ color: 'var(--color-primary)', fontWeight: 700 }}>{progressModal.processedTransactions}</span> transactions have been successfully imported.
                    </Typography>
                }
                <>
                    { step > 0 && step < 6 &&
                        <Typography textAlign="center" fontSize={14} fontWeight={500} mb={2}>
                            There are <span style={{ color: 'var(--color-primary)', fontWeight: 700 }}>{fileDetails?.result?.totalRows}</span> lines in the uploaded file, with <span style={{ color: 'var(--color-primary)', fontWeight: 700 }}>{fileDetails?.result?.successfulRows}</span> entries identified.
                        </Typography>
                    }
                    { step === 0 &&
                        <Typography textAlign="center" fontSize={14} fontWeight={500} mb={2}>
                            Select a bank account and file to begin the import process.
                        </Typography>
                    }
                    <Box display="flex" justifyContent="center" mb={2}>
                        <StepLineIndicator 
                            steps={7} 
                            activeStep={step} 
                            width={1000} 
                            stepNames={['Select File', 'Duplicate Checking', 'Provision Matching', 'Category Matching', 'Manual Categorization', 'Review', 'Import Results']}
                            forceCompletedSteps={(() => {
                                const completed = [];
                                if (!isProcessing && duplicateCount === 0 && hasProcessedStep[1]) completed.push(1);
                                if (!isProcessing && matchedCount.provision === 0 && hasProcessedStep[2]) completed.push(2);
                                if (!isProcessing && matchedCount.category === 0 && hasProcessedStep[3]) completed.push(3);
                                if (progressModal.status === 'success' || progressModal.status === 'error') completed.push(6);
                                return completed;
                            })()}
                        />
                    </Box>
                </>
                <Box display="flex" justifyContent="space-between" mt={4} mb={2}>
                    {step !== 0 && step !== 1 && step !== 6 &&
                        <Typography fontWeight={500} fontSize={14}>
                            {step === 5 
                                ? 'Review all new transaction entries and confirm when ready to proceed.' 
                                : step === 1 && isProcessing 
                                ? ""
                                : <span>Of the identified entries, <span style={{ color: 'var(--color-primary)', fontWeight: 700 }}>
                                    { (() => {
                                        if (step === 1) return matchedCount.duplicates;
                                        else if (step === 2) return matchedCount.provision;
                                        else if (step === 3) return matchedCount.category;
                                        else if (step === 4) return matchedCount.unmatched;
                                    })()}
                                </span> {step === 4 ? '' : 'matched existing'} {step === 1 ? 'transactions' : step === 2 ? 'provisions' : step === 4 ? 'transactions cannot be categorized' : 'categories'}</span>
                            }
                        </Typography>
                    }
                    { step === 1 && !isProcessing && (duplicateCount === 0) &&
                        <Typography fontWeight={500} fontSize={14}>
                            Of the <span style={{ color: 'var(--color-primary)', fontWeight: 700 }}>{fileDetails?.parsedRows?.length ?? 0}</span> transactions analyzed, none were duplicates. You're good to go!
                        </Typography>
                    }
                    { step === 6 && fileDetails?.failedRows && fileDetails.failedRows.length > 0 &&
                        <Typography fontWeight={500} fontSize={14}>
                            File of mismatched entries <span 
                                onClick={() => {
                                    const now = new Date();
                                    const yyyymmdd = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
                                    const formattedImportId = String(importId).padStart(3, '0');
                                    exportToCSV(
                                        fileDetails?.failedRows || [], 
                                        `UNRECOGNIZED_${yyyymmdd}${formattedImportId}`
                                    );
                                }}
                                style={{ 
                                    color: 'var(--color-primary)', 
                                    textDecoration: 'underline', 
                                    cursor: 'pointer' 
                                }}>UNRECOGNIZED_{(() => {
                                    const now = new Date();
                                    const yyyymmdd = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
                                    const formattedImportId = String(importId).padStart(3, '0');
                                    return `${yyyymmdd}${formattedImportId}`;
                                })()}</span>
                        </Typography>
                    }
                </Box>
                {tableRender[step]}
            </DialogContent>
            <Box sx={{ p: 2, borderTop: '1px solid #eee', display: 'flex', justifyContent: "space-between", gap: 2, flexShrink: 0, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Always-present left-side container, even when empty - without
                    it, justifyContent: space-between collapses to flex-start
                    (not flex-end) once every conditional block below happens
                    to be absent (e.g. a step with 0 rows: no "Approve all"
                    checkbox, no pagination, no step-specific tooltip), which
                    was pulling the Back/Next button group to the left instead
                    of keeping it pinned to the right. */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    { step === 3 && categoryMatchRows.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Checkbox
                                checked={allChecked()}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCheckAll(e.target.checked)}
                                sx={{ color: 'var(--color-primary)', '&.Mui-checked': { color: 'var(--color-primary)' }, height: 24, width: 24 }}
                            />
                            <Typography variant="body2" sx={{ userSelect: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleCheckAll(!allChecked())}>
                                Approve all
                            </Typography>
                        </Box>
                    )}
                    { step === 4 && categoryUnmatchedRows.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Checkbox
                                checked={allChecked()}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCheckAll(e.target.checked)}
                                sx={{ color: 'var(--color-primary)', '&.Mui-checked': { color: 'var(--color-primary)' }, height: 24, width: 24 }}
                            />
                            <Typography variant="body2" sx={{ userSelect: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleCheckAll(!allChecked())}>
                                Approve all
                            </Typography>
                        </Box>
                    )}
                    { step === 1 &&
                        <Box sx={{ alignSelf: 'center', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography fontSize={14} color="text.secondary">
                                Need to import all entries, including duplicates?
                            </Typography>
                            <Tooltip 
                                arrow 
                                placement="top"
                                sx={{
                                    '& .MuiTooltip-tooltip': {
                                        maxWidth: 400,
                                        fontSize: 13,
                                        p: 2
                                    }
                                }}
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            maxWidth: 400,
                                            fontSize: 13,
                                            p: 2,
                                            '& .MuiTooltip-arrow': {
                                                color: 'rgba(97, 97, 97, 0.9)'
                                            }
                                        }
                                    }
                                }}
                                PopperProps={{
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [0, -8],
                                            },
                                        },
                                    ],
                                }}
                                slotProps={{
                                    tooltip: {
                                        sx: {
                                            maxWidth: 400,
                                            fontSize: 13,
                                            p: 2,
                                            backgroundColor: 'rgba(255, 255, 255, 1)',
                                            boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
                                        }
                                    }
                                }}
                                title={
                                    <Box color="black">
                                        <Typography fontSize={13} fontWeight={600} mb={1} color="primary">
                                            How to Add Duplicate Entries:
                                        </Typography>
                                        <Typography fontSize={12} mb={1}>
                                            To import duplicates intentionally, upload the total number of rows you want (existing identical entries + new ones). The system removes exact matches and imports the rest.
                                        </Typography>
                                        <Typography fontSize={12}>
                                            <strong>Example:</strong> 3 exist → upload 8 rows → 5 are imported.
                                        </Typography>
                                    </Box>
                                }
                            >
                                <Typography 
                                    fontSize={14} 
                                    sx={{ 
                                        color: 'var(--color-primary)', 
                                        textDecoration: 'underline', 
                                        cursor: 'pointer',
                                        '&:hover': {
                                            color: '#008c7a'
                                        }
                                    }}
                                >
                                    Learn how
                                </Typography>
                            </Tooltip>
                        </Box>
                    }
                    { (step === 2 || step === 5 || step === 3 || step === 0) &&
                        <span style={{ width: 10 }}/>
                    }
                    { (step === 3 || step === 4 || step === 5) && ((step === 3 && Math.ceil(categoryMatchRows.length / rowsPerPage) > 1) || (step === 4 && Math.ceil(categoryUnmatchedRows.length / rowsPerPage) > 1) || (step === 5 && Math.ceil(approvedForImport.length / rowsPerPage) > 1)) && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
                            <Pagination 
                                count={step === 3 ? Math.ceil(categoryMatchRows.length / rowsPerPage) : step === 4 ? Math.ceil(categoryUnmatchedRows.length / rowsPerPage) : Math.ceil(approvedForImport.length / rowsPerPage)}
                                page={page[step] || 1}
                                onChange={(_, newPage) => handlePageChange(step, newPage)}
                                size="medium"
                                showFirstButton
                                showLastButton
                                sx={{
                                    '& .MuiPaginationItem-root': {
                                        color: '#666 !important',
                                        fontWeight: '500 !important',
                                        borderRadius: '4px',
                                        minWidth: '32px',
                                        height: '32px',
                                    },
                                    '& .MuiPaginationItem-root.Mui-selected': {
                                        backgroundColor: '#00b59e !important',
                                        color: '#fff !important',
                                    },
                                    '& .MuiPaginationItem-ellipsis': {
                                        color: 'var(--color-text-muted)',
                                        height: 'auto',
                                    },
                                }}
                            />
                        </Box>
                    )}
                </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                    { step > 0 && step < 6 &&
                        <Button
                            variant="contained"
                            sx={{ borderRadius: 2, minWidth: 120, fontSize: 14, py: 1.2, textTransform: 'none', color: 'var(--color-primary)', height: 35, background: 'var(--color-page-background)', border: '1px solid #00b59e' }}
                            onClick={()=> {
                                const prevStep = step - 1;
                                setStep(prevStep);
                                
                                // Reset page to 1 when going back to steps with pagination
                                if (prevStep >= 2 && prevStep <= 4) {
                                    setPage(prev => ({ ...prev, [prevStep]: 1 }));
                                }
                                
                                // Reset processing flags for the previous step to allow recalculation if needed
                                setHasProcessedStep(prev => {
                                    const newProcessed = { ...prev };
                                    // Only reset steps that need recalculation (1 and 2)
                                    if (prevStep <= 2) {
                                        newProcessed[prevStep] = false;
                                    }
                                    // Also reset steps after prevStep since they may depend on it
                                    for (let s = prevStep + 1; s <= 6; s++) {
                                        newProcessed[s] = false;
                                    }
                                    return newProcessed;
                                });
                                // Clear checked states for steps after prevStep
                                setCheckedStates(prev => {
                                    const newStates = { ...prev };
                                    for (let s = prevStep + 1; s <= 6; s++) {
                                        newStates[s] = {};
                                    }
                                    return newStates;
                                });
                                // Clear row data only for steps 1 and 2 to force recalculation when going back to them
                                if (prevStep === 1) {
                                    setProvisionMatchRows([]);
                                } else if (prevStep === 2) {
                                    setCategoryMatchRows([]);
                                    setCategoryUnmatchedRows([]);
                                    setOriginalUnmatchedRows([]);
                                }
                            }}
                        >
                            Back
                        </Button>
                    }
                    { step === 6 && (
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ 
                                borderRadius: 2, 
                                minWidth: 120, 
                                fontSize: 14, 
                                py: 1.2, 
                                textTransform: 'none', 
                                color: '#fff', 
                                height: 35,
                                background: 'var(--color-primary)',
                            }}
                            onClick={() => {
                                onClose();
                                setStep(0);
                            }}
                        >
                            Close
                        </Button>
                    )}
                    { step < 5 && (
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={Boolean(isProcessing || (step === 0 && (!file || fileErrors.file || !fileDetails?.headers)))}
                            sx={{ 
                                borderRadius: 2, 
                                minWidth: 120, 
                                fontSize: 14, 
                                py: 1.2, 
                                textTransform: 'none', 
                                color: '#fff', 
                                height: 35,
                                background: Boolean(isProcessing || (step === 0 && (!file || fileErrors.file || !fileDetails?.headers))) ? '#ccc' : 'var(--color-primary)',
                            }}
                            onClick={async ()=> {
                                // Validate file selection at step 0
                                if (step === 0) {
                                    if (!validateFileSelection()) {
                                        return;
                                    }
                                }
                                
                                const nextStep = step + 1;
                                setStep(nextStep);
                                
                                // Reset page to 1 when moving to steps with pagination
                                if (nextStep >= 2 && nextStep <= 4) {
                                    setPage(prev => ({ ...prev, [nextStep]: 1 }));
                                }
                                
                                // Step 4: rebuild to show unchecked rows from step 3 + unmatched rows
                                if (nextStep === 4) {
                                    setIsProcessing(true);
                                    await new Promise(resolve => setTimeout(resolve, 10));
                                    buildStep3Rows();
                                    setIsProcessing(false);
                                } else if (nextStep === 5) {
                                    // Step 5 is just review, no processing needed
                                    // Don't reset isProcessing here - let the effect handle it
                                }
                                // For steps 1, 2, 3: effects handle processing automatically
                            }}
                        >
                            {isProcessing ? (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <CircularProgress size={16} sx={{ color: '#fff' }} />
                                    <span>Processing...</span>
                                </Box>
                            ) : (
                                'Next'
                            )}
                        </Button>
                    )}
                    { step === 5 && (
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={isProcessing}
                            sx={{ 
                                borderRadius: 2, 
                                minWidth: 120, 
                                fontSize: 14, 
                                py: 1.2, 
                                textTransform: 'none', 
                                color: '#fff', 
                                height: 35,
                                background: isProcessing ? '#ccc' : 'var(--color-primary)',
                            }}
                            onClick={() => {
                                if (importId) {
                                    setStep(6);
                                    onConfirm(approvedForImport, importId);
                                } else {
                                    showError('Import ID not available. Please try again.');
                                }
                            }}
                        >
                            {isProcessing ? (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <CircularProgress size={16} sx={{ color: '#fff' }} />
                                </Box>
                            ) : (
                                'Confirm Import'
                            )}
                        </Button>
                    )}
                    </Box>
                </Box>
            </Dialog>
        );
    };

    export default ImportMatchPopup;
